/**
 * @module engine3d/models/kit
 * @summary PREMIUM KIT v2 — the shared sub-assemblies every 3D equipment model
 *   is built from, the segment policy, the paint-zone table and the model
 *   contract types.
 * @description
 *   Real plant equipment repeats the same handful of features — a base frame,
 *   legs on base plates, a flanged nozzle, a TEFC motor, a gearbox, a caged
 *   ladder, a control cabinet. Modelling those ONCE here, to one set of
 *   proportions and one paint table, is what makes ninety-odd symbols look like
 *   they came out of the same factory, and keeps each model in `models/*.ts`
 *   down to a readable composition of named parts.
 *
 *   Conventions (every helper, unless its doc says otherwise):
 *     • Local origin centred in X/Z, resting on the floor (y = 0), +Y is height.
 *     • Process flow runs along +X; the operator side is −Z.
 *     • Every helper returns FRESH meshes, so builders may translate/rotate
 *       them in place and the model cache is never aliased.
 *     • Every mesh carries `feat` (edge data, see `mesh.ts`), so the renderer
 *       can draw crisp CAD-style feature lines on the still frame.
 *     • Multi-material helpers return `Part[]` with materials from `ZONE`
 *       (override any zone with `mat`); single-material helpers return a Mesh
 *       and the caller picks the material.
 *     • Triangle costs quoted in the docs are as BUILT (≈ half are drawn after
 *       back-face culling). The API cheat-sheet (.claude/premium/kit-api.md)
 *       lists every helper with its cost.
 *
 *   The v1 exports (`motorUnit`, `gearbox`, `flange`, `nozzle`, `legs`, …) keep
 *   their signatures and their v1 bounding boxes (+0/−5 %, base plates ≤ 1.5
 *   units more in plan), so every existing builder improves without moving a
 *   single extent or port.
 *
 *   // INVARIANT: builders are pure functions of (Dims, detail). The ONLY module
 *   //   state here is the detail level, set by `withDetail` for the length of
 *   //   one synchronous build (models/index.ts getModelExact).
 *   // INVARIANT: an in-place (v1) helper stays inside its v1 bounding box
 *   //   (+0/−5 % per axis; base plates may add ≤ 1.5 in plan) — that is what
 *   //   keeps `groundModel` from lifting any model and every port in place.
 *   // HAZARD: revolve profiles must run bottom→top along their axis; a profile
 *   //   written top→bottom comes out inside-out (normals point inward).
 *   // HAZARD: never mirror with a negative scale — it flips winding and every
 *   //   stored dihedral. Rotate by π instead (`rotateY(m, Math.PI)`).
 */
import type { PortKey } from '../../types';
import type { MatKey } from '../materials';
import {
  bevelBox,
  bounds,
  box,
  boxCentered,
  cylinder,
  cylinderX,
  cylinderZ,
  decal,
  densify,
  emptyMesh,
  extrude,
  featurize,
  filletArc,
  filletPath,
  frustum,
  cone,
  helicoid,
  hexPrism,
  lined,
  merge,
  ngon,
  prism,
  quad,
  rectPoly,
  revolve,
  ring,
  rotateX,
  rotateY,
  rotateZ,
  roundBox,
  scale,
  segForLod,
  soft,
  sphere,
  stadium,
  sweep,
  torusArc,
  translate,
  tri,
  triangulate,
  tube,
  F_DECAL,
  F_PAIR,
  featDih,
  featEdges,
  type Mesh,
  type ProfilePt,
  type Role,
} from '../mesh';
import { TAU, v3, vAdd, vCross, vDot, vMul, vNorm, vSub, type Vec2, type Vec3 } from '../math3';

// ─── Model contract ────────────────────────────────────────────────────────

/** One mesh + the material it is drawn with. */
export interface Part {
  mesh: Mesh;
  mat: MatKey;
}

/** A connection point on a model: position + outward normal, in local space. */
export interface Port3 {
  p: Vec3;
  n: Vec3;
  /** Nozzle bore radius, when the port is a real nozzle (kit §4.6). Optional. */
  r?: number;
}

/** A fully built 3D equipment model in local space. */
export interface Model3 {
  parts: Part[];
  /** Total +Y extent — used for label placement and camera fitting. */
  height: number;
  /** Where pipework docks. Missing keys fall back to the bounding box face. */
  ports: Partial<Record<PortKey, Port3>>;
  /** Convex ground outline (local XZ) used to cast the shadow. */
  footprint: Vec2[];
}

/** The X / Y / Z extents a builder must fill. */
export interface Dims {
  /** X extent — from the 2D symbol's width. */
  w: number;
  /** Y extent (height) — from the 2D symbol's height. */
  h: number;
  /** Z extent (depth) — per-type, since the 2D drawing has no third axis. */
  d: number;
  /**
   * Optional discriminator for symbols that have more than one 3D form — a
   * zone box drawn as a floor area, a wall, or a room. It is part of the model
   * cache key, so each form is built and reused separately.
   */
  variant?: string;
}

export type Builder = (d: Dims) => Model3;

/**
 * A builder a family module registers from its OWN file (kit decision D5):
 * each `models/<family>.ts` may export `EXTRA_MODELS: ExtraModels`, and
 * `models/index.ts` merges every family's table into the registry, so the new
 * model appears in the 3D Model picker (`Shape.model3`) with no edit to
 * index.ts — and, by design, is reachable ONLY by being picked there: nothing
 * infers it from a type or a label.
 *
 *   depth: 'round' — as deep as it is wide (vessels, reels on end);
 *          'plan'  — the 2D box is a footprint, Z = 2D h;
 *          number  — Z = max(16, 2D h × k), for long machines you walk beside.
 *          Absent  — the default proportion of the 2D footprint.
 */
export interface ExtraModel {
  build: Builder;
  label: string;
  depth?: 'round' | 'plan' | number;
}
export type ExtraModels = Record<string, ExtraModel>;

/** Sugar for building the `parts` array. */
export const p = (mesh: Mesh, mat: MatKey): Part => ({ mesh, mat });

/** Merge several same-material meshes into one part. */
export const pm = (mat: MatKey, ...meshes: Mesh[]): Part => ({ mesh: merge(...meshes), mat });

type Axis = 'x' | 'y' | 'z';

// ─── Detail context and segment policy (§2.1, §6.3) ────────────────────────

let LOD: 0 | 1 = 0;

/**
 * Run one synchronous model build at a detail level. Used ONLY by
 * `models/index.ts` (getModelExact), which also puts the level in the cache
 * key — so a builder stays a pure function of (Dims, lod).
 */
export function withDetail<T>(lod: 0 | 1, fn: () => T): T {
  const prev = LOD;
  LOD = lod;
  try {
    return fn();
  } finally {
    LOD = prev;
  }
}

/** The current detail level: 0 = full (v2 always), 1 = reduced (§6.3, off). */
export const detail = (): 0 | 1 => LOD;

/**
 * Segments round a circle of radius `r` for a role — ALWAYS a multiple of 4, so
 * E/W/N/S ports land on a vertex and nested parts line up facet to facet.
 * hero 24–48 (the surface that IS the machine), body 16–32, detail 8–16,
 * fine 8.
 */
export const segFor = (r: number, role: Role): number => segForLod(r, role, LOD);

// ─── Paint zones (§7.3) ────────────────────────────────────────────────────

export type Zone =
  | 'skin' | 'base' | 'trim' | 'hotSteel' | 'cast' | 'drive' | 'product' | 'shell'
  | 'frame' | 'guard' | 'actuator' | 'cabinet' | 'bezel' | 'display' | 'signal' | 'cavity'
  | 'glass' | 'rubber' | 'alu' | 'bright' | 'fastener' | 'heater';

export type ZoneMap = Partial<Record<Zone, MatKey>>;

/**
 * The ONE table that maps design-language zones to materials. A family never
 * hard-codes "this is grey"; it says "this is a drive" and the whole library
 * repaints together. Never: orange on structure, yellow on product contact,
 * navy on stainless vessels.
 */
export const ZONE: Record<Zone, MatKey> = {
  skin: 'painted', // enclosures, hoods and covers of line machines (JCC navy)
  base: 'machineDark', // base frames, barrel supports, pedestals
  trim: 'machine', // adapters, brackets, bearing housings, idlers
  hotSteel: 'machineDark', // barrels, dies, heads
  cast: 'cast', // pump, valve and rotary-valve bodies
  drive: 'motor', // motors, gearboxes, lanterns (RAL 7031-ish)
  product: 'stainless', // hoppers, vessels, chutes, product pipe, name plates
  shell: 'steel', // large silo walls
  frame: 'galv', // legs, bracing, ring beams, platforms, ladders, supports
  guard: 'safety', // handrails, toe boards, coupling and belt guards
  actuator: 'accent', // valve actuators, handwheels, levers (operator controls)
  cabinet: 'cabinet', // RAL 7035 enclosures, consoles, junction boxes
  bezel: 'bezel', // HMI bezels, plinths, glands
  display: 'display', // HMI glass
  signal: 'signalRed', // e-stop mushrooms, red stack-light tier
  cavity: 'cavity', // openings, slots, grilles, bores — painted-in occlusion
  glass: 'glass', // sight glasses, windows
  rubber: 'rubber', // belts, tyres, gaskets, hoses, bellows
  alu: 'alu', // heater shells, cylinder barrels, gauge heads
  bright: 'chrome', // piston rods, godets, chill rolls, shafts
  fastener: 'machineDark', // bolts and nuts on painted parts (use 'steel' on stainless)
  heater: 'hot', // heater BANDS (heater SHELLS are `alu`)
};

/** The material for a zone, honouring an optional per-call override. */
export const zm = (z: Zone, over?: ZoneMap): MatKey => over?.[z] ?? ZONE[z];

// ─── Assembly utilities (§3.4) ─────────────────────────────────────────────

type PartIn = Part | Part[] | false | null | undefined;

/**
 * Flatten parts and MERGE the same-material ones (first-appearance order), so
 * a model has one part per material. Falsy entries and empty meshes are
 * dropped, which lets builders write `cond && part` inline.
 */
export function assy(...items: PartIn[]): Part[] {
  const order: MatKey[] = [];
  const by = new Map<MatKey, Mesh[]>();
  const add = (pt: Part | null | undefined | false) => {
    if (!pt || !pt.mesh || !pt.mesh.idx.length) return;
    let l = by.get(pt.mat);
    if (!l) {
      l = [];
      by.set(pt.mat, l);
      order.push(pt.mat);
    }
    l.push(pt.mesh);
  };
  for (const it of items) {
    if (!it) continue;
    if (Array.isArray(it)) it.forEach(add);
    else add(it);
  }
  return order.map((mat) => {
    const l = by.get(mat)!;
    return { mat, mesh: l.length === 1 ? l[0] : merge(...l) };
  });
}

/** Translate every part in place; returns the same array. */
export function moveA(a: Part[], x: number, y: number, z: number): Part[] {
  for (const q of a) translate(q.mesh, x, y, z);
  return a;
}

/** Rotate every part about a world axis through the origin, in place. */
export function turnA(a: Part[], axis: Axis, ang: number): Part[] {
  const f = axis === 'x' ? rotateX : axis === 'y' ? rotateY : rotateZ;
  for (const q of a) f(q.mesh, ang);
  return a;
}

/**
 * Aim a mesh built along local +Y so that +Y points along `n`, then move it to
 * `at`. For a horizontal `n`, local +Z ends up pointing DOWN (−Y) and local X
 * stays horizontal — so a plate built in local XZ reads w wide, h tall.
 */
export function aim(m: Mesh, at: Vec3, n: Vec3): Mesh {
  const l = Math.hypot(n.x, n.y, n.z) || 1;
  const pitch = Math.acos(Math.max(-1, Math.min(1, n.y / l)));
  const yaw = Math.atan2(n.x, n.z);
  rotateX(m, pitch);
  rotateY(m, yaw);
  return translate(m, at.x, at.y, at.z);
}

/** `aim` for a whole part list. */
export function aimA(a: Part[], at: Vec3, n: Vec3): Part[] {
  for (const q of a) aim(q.mesh, at, n);
  return a;
}

/** Re-orient a mesh built along +Y onto another axis (+Y → +X or +Z). */
function yTo(m: Mesh, axis: Axis): Mesh {
  if (axis === 'x') rotateZ(m, -Math.PI / 2);
  else if (axis === 'z') rotateX(m, Math.PI / 2);
  return m;
}

/** Re-orient a mesh built along +X onto another axis (+X → +Y or +Z). */
function xTo(m: Mesh, axis: Axis): Mesh {
  if (axis === 'y') rotateZ(m, Math.PI / 2);
  else if (axis === 'z') rotateY(m, -Math.PI / 2);
  return m;
}

const clampN = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// ─── 2D sections (x = local right, y = local up) ───────────────────────────

/** A w × h rectangle centred on the origin. */
export function secRect(w: number, h: number): Vec2[] {
  return [
    { x: -w / 2, y: -h / 2 },
    { x: w / 2, y: -h / 2 },
    { x: w / 2, y: h / 2 },
    { x: -w / 2, y: h / 2 },
  ];
}

/** A w × h rectangle with its four corners chamfered by `c` (8 points). */
export function secChamferRect(w: number, h: number, c: number): Vec2[] {
  const x = w / 2;
  const y = h / 2;
  c = Math.max(0, Math.min(c, 0.45 * Math.min(w, h)));
  return [
    { x: -x + c, y: -y },
    { x: x - c, y: -y },
    { x, y: -y + c },
    { x, y: y - c },
    { x: x - c, y },
    { x: -x + c, y },
    { x: -x, y: y - c },
    { x: -x, y: -y + c },
  ];
}

/** A circle of `n` points (first point on +x, so n % 4 === 0 puts one on each axis). */
export function secCircle(r: number, n: number): Vec2[] {
  return ngon(r, n);
}

/** UPN-like channel, toes pointing to local +x, bbox centred. */
function secC(h: number, b: number, t: number): Vec2[] {
  t = Math.min(t, 0.45 * b, 0.3 * h);
  const x0 = -b / 2;
  const x1 = b / 2;
  const y0 = -h / 2;
  const y1 = h / 2;
  return [
    { x: x0, y: y0 },
    { x: x1, y: y0 },
    { x: x1, y: y0 + t },
    { x: x0 + t, y: y0 + t },
    { x: x0 + t, y: y1 - t },
    { x: x1, y: y1 - t },
    { x: x1, y: y1 },
    { x: x0, y: y1 },
  ];
}

/** HEB-like I/H section: web along local y, flanges along local x. */
function secH(h: number, b: number, tw: number, tf: number): Vec2[] {
  const x = b / 2;
  const y = h / 2;
  tw = Math.min(tw, 0.4 * b);
  tf = Math.min(tf, 0.3 * h);
  return [
    { x: -x, y: -y },
    { x, y: -y },
    { x, y: -y + tf },
    { x: tw / 2, y: -y + tf },
    { x: tw / 2, y: y - tf },
    { x, y: y - tf },
    { x, y },
    { x: -x, y },
    { x: -x, y: y - tf },
    { x: -tw / 2, y: y - tf },
    { x: -tw / 2, y: -y + tf },
    { x: -x, y: -y + tf },
  ];
}

/** Equal angle, heel at the bbox's lower-left, bbox centred. */
function secL(a: number, t: number): Vec2[] {
  t = Math.min(t, 0.4 * a);
  const o = -a / 2;
  return [
    { x: o, y: o },
    { x: o + a, y: o },
    { x: o + a, y: o + t },
    { x: o + t, y: o + t },
    { x: o + t, y: o + a },
    { x: o, y: o + a },
  ];
}

function rotSec(sec: Vec2[], ang: number): Vec2[] {
  if (!ang) return sec;
  const c = Math.cos(ang);
  const s = Math.sin(ang);
  return sec.map((q) => ({ x: q.x * c - q.y * s, y: q.x * s + q.y * c }));
}

function secMinY(sec: Vec2[]): number {
  let m = Infinity;
  for (const q of sec) m = Math.min(m, q.y);
  return m;
}

/** Signed area (positive = CCW). */
function area2(poly: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p0 = poly[i];
    const p1 = poly[(i + 1) % poly.length];
    a += p0.x * p1.y - p1.x * p0.y;
  }
  return a / 2;
}

// ─── Low-level builders shared by the helpers ──────────────────────────────

/**
 * Quads between consecutive rows of points (each row a closed loop of the same
 * length). Hints point away from the loft axis, so the result is outward-facing
 * for any star-shaped section. Rows/columns listed in `featRows`/`featCols` are
 * authored feature lines (weld seams, split lines).
 */
function loftRows(
  rows: Vec3[][],
  o: { axis: Axis; featRows?: number[]; featCols?: number[]; open?: boolean } = { axis: 'x' },
): Mesh {
  const m = emptyMesh();
  const fr = new Set(o.featRows ?? []);
  const fc = new Set(o.featCols ?? []);
  const n = rows[0]?.length ?? 0;
  const cols = o.open ? n - 1 : n;
  for (let j = 0; j + 1 < rows.length; j++)
    for (let i = 0; i < cols; i++) {
      const i1 = (i + 1) % n;
      const a = rows[j][i];
      const b = rows[j][i1];
      const c = rows[j + 1][i1];
      const d = rows[j + 1][i];
      const ctr = vMul(vAdd(vAdd(a, b), vAdd(c, d)), 0.25);
      const hint = o.axis === 'x' ? v3(0, ctr.y, ctr.z) : o.axis === 'y' ? v3(ctr.x, 0, ctr.z) : v3(ctr.x, ctr.y, 0);
      const edges = (fr.has(j) ? 1 : 0) | (fc.has(i1) ? 2 : 0) | (fr.has(j + 1) ? 4 : 0) | (fc.has(i) ? 8 : 0);
      quad(m, a, b, c, d, { hint, edges });
    }
  return featurize(m);
}

/** A section (right = +Z, up = +Y) placed at position x on the X axis. */
const secAtX = (sec: Vec2[], x: number): Vec3[] => sec.map((q) => v3(x, q.y, q.x));

/** Fan-cap a closed section at x, facing ±X. */
function capX(m: Mesh, sec: Vec2[], x: number, dir: 1 | -1): void {
  const T = triangulate(sec);
  for (let t = 0; t < T.length; t += 3)
    tri(m, v3(x, sec[T[t]].y, sec[T[t]].x), v3(x, sec[T[t + 1]].y, sec[T[t + 1]].x), v3(x, sec[T[t + 2]].y, sec[T[t + 2]].x), {
      n: v3(dir, 0, 0),
    });
}

/**
 * A 2D outline (local XY) extruded along Z (centred), with every edge of both
 * faces chamfered by `ch` — guards, volutes, plates with a cast or pressed
 * edge. Caps are ear-clipped, so concave outlines work.
 * Tris: 2n (ch = 0) or 6n (chamfered) + 2(n − 2) for the caps.
 */
export function slab(outline: Vec2[], depth: number, ch = 0, o: { caps?: boolean } = {}): Mesh {
  const m = emptyMesh();
  const n = outline.length;
  if (n < 3) return m;
  const sgn = area2(outline) >= 0 ? 1 : -1;
  const en: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const a = outline[i];
    const b = outline[(i + 1) % n];
    const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    en.push({ x: ((b.y - a.y) / l) * sgn, y: (-(b.x - a.x) / l) * sgn });
  }
  ch = Math.max(0, Math.min(ch, depth * 0.45));
  const inner =
    ch > 0
      ? outline.map((q, i) => {
          const n1 = en[(i - 1 + n) % n];
          const n2 = en[i];
          const k = Math.max(0.3, 1 + n1.x * n2.x + n1.y * n2.y);
          return { x: q.x - (ch * (n1.x + n2.x)) / k, y: q.y - (ch * (n1.y + n2.y)) / k };
        })
      : outline;
  const hz = depth / 2;
  const rows: { z: number; pts: Vec2[] }[] =
    ch > 0
      ? [
          { z: -hz, pts: inner },
          { z: -hz + ch, pts: outline },
          { z: hz - ch, pts: outline },
          { z: hz, pts: inner },
        ]
      : [
          { z: -hz, pts: outline },
          { z: hz, pts: outline },
        ];
  for (let j = 0; j + 1 < rows.length; j++) {
    const zc = ch > 0 ? (j === 0 ? -0.7 : j === rows.length - 2 ? 0.7 : 0) : 0;
    for (let i = 0; i < n; i++) {
      const i1 = (i + 1) % n;
      const A = rows[j].pts;
      const B = rows[j + 1].pts;
      quad(m, v3(A[i].x, A[i].y, rows[j].z), v3(A[i1].x, A[i1].y, rows[j].z), v3(B[i1].x, B[i1].y, rows[j + 1].z), v3(B[i].x, B[i].y, rows[j + 1].z), {
        hint: v3(en[i].x, en[i].y, zc),
      });
    }
  }
  if (o.caps !== false) {
    const T = triangulate(inner);
    for (const z of [-hz, hz])
      for (let t = 0; t < T.length; t += 3)
        tri(m, v3(inner[T[t]].x, inner[T[t]].y, z), v3(inner[T[t + 1]].x, inner[T[t + 1]].y, z), v3(inner[T[t + 2]].x, inner[T[t + 2]].y, z), {
          n: v3(0, 0, Math.sign(z)),
        });
  }
  return featurize(m);
}

/**
 * Flag as FEATURE every triangle edge lying on one of the rows y in `ys` (both
 * ends at that height) — re-instating rim lines after `soft`.
 */
function markRowsY(m: Mesh, ys: number[], tol = 1e-4): Mesh {
  const f = m.feat ?? [];
  const P = m.pos;
  const I = m.idx;
  for (let t = 0; t < I.length / 3; t++)
    for (let k = 0; k < 3; k++) {
      const ya = P[I[t * 3 + k] * 3 + 1];
      const yb = P[I[t * 3 + ((k + 1) % 3)] * 3 + 1];
      if (ys.some((y) => Math.abs(ya - y) < tol && Math.abs(yb - y) < tol)) f[t] = ((f[t] | (1 << k)) & ~(8 << k)) >>> 0;
    }
  m.feat = f;
  return m;
}

/** A flat, one-sided quad plate facing `n` (for decals: slots, screens, grilles). */
function plateQuad(c: Vec3, u: Vec3, v: Vec3, n: Vec3): Mesh {
  const m = emptyMesh();
  const a = vSub(vSub(c, u), v);
  const b = vSub(vAdd(c, u), v);
  const cc = vAdd(vAdd(c, u), v);
  const d = vAdd(vSub(c, u), v);
  quad(m, a, b, cc, d, { n, edges: 15 });
  return featurize(m);
}

/** A thin box without its back face (−Y): 10 tris. Plates, leaves, labels. */
function openBox(w: number, h: number, d: number): Mesh {
  const m = emptyMesh();
  const x = w / 2;
  const z = d / 2;
  quad(m, v3(-x, h, z), v3(x, h, z), v3(x, h, -z), v3(-x, h, -z), { n: v3(0, 1, 0), edges: 15 });
  quad(m, v3(-x, 0, z), v3(x, 0, z), v3(x, h, z), v3(-x, h, z), { n: v3(0, 0, 1), edges: 15 });
  quad(m, v3(x, 0, -z), v3(-x, 0, -z), v3(-x, h, -z), v3(x, h, -z), { n: v3(0, 0, -1), edges: 15 });
  quad(m, v3(x, 0, z), v3(x, 0, -z), v3(x, h, -z), v3(x, h, z), { n: v3(1, 0, 0), edges: 15 });
  quad(m, v3(-x, 0, -z), v3(-x, 0, z), v3(-x, h, z), v3(-x, h, -z), { n: v3(-1, 0, 0), edges: 15 });
  return featurize(m);
}

// ─── Ports ────────────────────────────────────────────────────────────────

/**
 * The default port set: the mid-height centre of each bounding-box face.
 * Models override individual keys where the real nozzle is somewhere else — a
 * silo discharges from its cone apex, an extruder is fed from the top.
 */
export function boxPorts(d: Dims, y = d.h / 2): Partial<Record<PortKey, Port3>> {
  return {
    E: { p: v3(d.w / 2, y, 0), n: v3(1, 0, 0) },
    W: { p: v3(-d.w / 2, y, 0), n: v3(-1, 0, 0) },
    S: { p: v3(0, y, d.d / 2), n: v3(0, 0, 1) },
    N: { p: v3(0, y, -d.d / 2), n: v3(0, 0, -1) },
  };
}

/** Port set for a vertical vessel: side nozzles on the barrel, feed on top. */
export function vesselPorts(
  d: Dims,
  opts: { sideY?: number; topY?: number; botY?: number; r?: number } = {},
): Partial<Record<PortKey, Port3>> {
  const r = opts.r ?? d.w / 2;
  const sy = opts.sideY ?? d.h * 0.62;
  return {
    E: { p: v3(r, sy, 0), n: v3(1, 0, 0) },
    W: { p: v3(-r, sy, 0), n: v3(-1, 0, 0) },
    N: { p: v3(0, opts.topY ?? d.h, 0), n: v3(0, 1, 0) },
    S: { p: v3(0, opts.botY ?? 0, 0), n: v3(0, -1, 0) },
  };
}

// ─── Footprints ──────────────────────────────────────────────────────────

export const rectFoot = (w: number, d: number): Vec2[] => rectPoly(w, d);
export const roundFoot = (r: number, n = 14): Vec2[] => ngon(r, n);

// ─── Vessel profiles (§3.5) ────────────────────────────────────────────────

export type HeadKind = '2:1' | 'klopper' | 'korbbogen' | 'hemi' | 'flat';

/** Meridian steps per head kind and role (§2.2). */
function headSteps(kind: HeadKind, role: Role): [number, number] {
  const r = role === 'hero' ? 0 : role === 'body' ? 1 : 2;
  if (kind === 'hemi') return [[8, 5, 3][r], 0];
  if (kind === '2:1') return [[6, 4, 3][r], 0];
  return [[3, 2, 1][r], [3, 2, 2][r]];
}

/**
 * A vessel head profile from the tangent line (R, 0) up to the apex
 * (0, sf + depth). The first point is a feature (the head-to-shell weld);
 * `sf` is the straight flange (default 0.04 R). Depths: 2:1 R/2, Klöpper
 * ≈ 0.388 R (DIN 28011), Korbbogen ≈ 0.51 R (DIN 28013), hemi R, flat a 45°
 * lip of 0.03 R. Approximate standard forms — verify against a head supplier
 * where an exact depth matters.
 */
export function headProfile(kind: HeadKind, R: number, o: { steps?: number; sf?: number; role?: Role } = {}): ProfilePt[] {
  const role = o.role ?? 'hero';
  const sf = Math.max(0, o.sf ?? 0.04 * R);
  const out: ProfilePt[] = [{ x: R, y: 0, e: true }];
  if (sf > 1e-6) out.push({ x: R, y: sf });
  if (kind === 'flat') {
    const lip = 0.03 * R;
    out.push({ x: R - lip, y: sf + lip, e: true }, { x: 0, y: sf + lip });
    return out;
  }
  if (kind === '2:1' || kind === 'hemi') {
    const b = kind === 'hemi' ? R : R / 2;
    const st = o.steps ?? headSteps(kind, role)[0];
    for (let i = 1; i <= st; i++) {
      const t = (i / st) * (Math.PI / 2);
      out.push({ x: i === st ? 0 : R * Math.cos(t), y: sf + b * Math.sin(t) });
    }
    return out;
  }
  const Rc = kind === 'klopper' ? 2 * R : 1.6 * R;
  const rk = kind === 'klopper' ? 0.2 * R : 0.308 * R;
  let [kS, cS] = headSteps(kind, role);
  if (o.steps) {
    kS = Math.max(1, Math.ceil(o.steps / 2));
    cS = Math.max(1, o.steps - kS);
  }
  const yc = Math.sqrt((Rc - rk) ** 2 - (R - rk) ** 2);
  const phi1 = Math.atan2(yc, R - rk);
  for (let i = 1; i <= kS; i++) {
    const f = (phi1 * i) / kS;
    out.push({ x: R - rk + rk * Math.cos(f), y: sf + rk * Math.sin(f) });
  }
  for (let i = 1; i <= cS; i++) {
    const f = phi1 + ((Math.PI / 2 - phi1) * i) / cS;
    out.push({ x: i === cS ? 0 : Rc * Math.cos(f), y: sf - yc + Rc * Math.sin(f) });
  }
  return out;
}

/** The depth of a head above its tangent line (excluding the straight flange). */
export function headDepth(kind: HeadKind, R: number): number {
  const hp = headProfile(kind, R, { sf: 0 });
  return hp[hp.length - 1].y;
}

/**
 * A conical bottom from the outlet (rOut, 0) up to the tangent line (R, h),
 * with a toroidal knuckle of radius `knuckle` (default 0.16 R; 0 = a sharp,
 * featured crease). The outlet row and both knuckle weld lines are features.
 */
export function coneProfile(
  R: number,
  rOut: number,
  h: number,
  o: { knuckle?: number; steps?: number; role?: Role } = {},
): ProfilePt[] {
  const role = o.role ?? 'hero';
  let rk = o.knuckle ?? 0.16 * R;
  rk = Math.max(0, Math.min(rk, 0.45 * Math.min(R - rOut, h)));
  const steps = o.steps ?? (role === 'hero' ? 3 : role === 'body' ? 2 : 0);
  const out: ProfilePt[] = [{ x: rOut, y: 0, e: true }];
  const A = R - rk - rOut;
  const B = h;
  const L = Math.hypot(A, B);
  if (rk < 1e-6 || steps < 1 || L <= rk * 1.01) {
    out.push({ x: R, y: h, e: true });
    return out;
  }
  const a = Math.atan2(B, A) - Math.asin(rk / L);
  const cx = R - rk;
  const f0 = a - Math.PI / 2;
  for (let i = 0; i <= steps; i++) {
    const f = f0 * (1 - i / steps);
    out.push({ x: i === steps ? R : cx + rk * Math.cos(f), y: i === steps ? h : h + rk * Math.sin(f), e: i === 0 || i === steps });
  }
  return out;
}

export interface VesselSpec {
  /** Barrel radius. */
  R: number;
  /** Barrel tangent lines (bottom, top). */
  y0: number;
  y1: number;
  bottom: { kind: HeadKind | 'cone'; h?: number; rOut?: number; knuckle?: number };
  top: { kind: HeadKind | 'cone'; h?: number; rOut?: number };
  /** Shell courses → featured seam rows at zero triangle cost. */
  courses?: number;
  role?: Role;
  /** Override the segment count (default segFor(R, role)). */
  seg?: number;
}

/**
 * One revolve for bottom + barrel + top of a vertical vessel. Course seams,
 * head welds and knuckle welds are feature rows, so the shell reads as
 * fabricated plate at no extra cost. Returns the tangent lines and the
 * outlet / apex heights for placing nozzles.
 * Tris ≈ 2 · seg · (profile rows − 1): silo R = 25 → ≈ 580.
 */
export function vesselShell(s: VesselSpec): { shell: Mesh; tangentYs: number[]; bottomY: number; topY: number } {
  const role = s.role ?? 'hero';
  const R = s.R;
  const seg = s.seg ?? segFor(R, role);
  const prof: ProfilePt[] = [];
  let bottomY: number;
  if (s.bottom.kind === 'cone') {
    const h = s.bottom.h ?? 0.8 * R;
    const cp = coneProfile(R, s.bottom.rOut ?? 0.15 * R, h, { knuckle: s.bottom.knuckle, role });
    for (const q of cp) prof.push({ x: q.x, y: s.y0 - h + q.y, e: q.e });
    bottomY = s.y0 - h;
  } else {
    const hp = headProfile(s.bottom.kind, R, { role });
    for (let i = hp.length - 1; i >= 0; i--) prof.push({ x: hp[i].x, y: s.y0 - hp[i].y, e: hp[i].e });
    bottomY = s.y0 - hp[hp.length - 1].y;
  }
  const c = Math.max(1, Math.round(s.courses ?? 1));
  for (let k = 1; k < c; k++) prof.push({ x: R, y: s.y0 + ((s.y1 - s.y0) * k) / c, e: true });
  prof.push({ x: R, y: s.y1, e: true });
  let topY: number;
  if (s.top.kind === 'cone') {
    const h = s.top.h ?? 0.25 * R;
    const ro = s.top.rOut ?? 0.12 * R;
    prof.push({ x: ro, y: s.y1 + h, e: true });
    if (ro > 1e-6) prof.push({ x: 0, y: s.y1 + h });
    topY = s.y1 + h;
  } else {
    const hp = headProfile(s.top.kind, R, { role });
    for (let i = 1; i < hp.length; i++) prof.push({ x: hp[i].x, y: s.y1 + hp[i].y, e: hp[i].e });
    topY = s.y1 + hp[hp.length - 1].y;
  }
  return { shell: revolve(prof, seg, true), tangentYs: [s.y0, s.y1], bottomY, topY };
}

// ─── Structural steel (§3.6) ──────────────────────────────────────────────

type Toes = '+x' | '-x' | '+y' | '-y' | '+z' | '-z';
const TOE: Record<Toes, Vec3> = {
  '+x': v3(1, 0, 0),
  '-x': v3(-1, 0, 0),
  '+y': v3(0, 1, 0),
  '-y': v3(0, -1, 0),
  '+z': v3(0, 0, 1),
  '-z': v3(0, 0, -1),
};

/** Local frame of a member along `axis` (right = d × up, up). */
function axisFrame(axis: Axis): { d: Vec3; up: Vec3; right: Vec3 } {
  const d = axis === 'x' ? v3(1, 0, 0) : axis === 'y' ? v3(0, 1, 0) : v3(0, 0, 1);
  const up = axis === 'y' ? v3(0, 0, 1) : v3(0, 1, 0);
  return { d, up, right: vNorm(vCross(d, up)) };
}

/**
 * Run a section along an axis: horizontal members sit ON the floor (bbox
 * bottom at y = 0) centred on the other axes; vertical members rise from 0.
 */
function axisMember(sec: Vec2[], len: number, axis: Axis, toe: Vec3 | null): Mesh {
  const f = axisFrame(axis);
  let s = sec;
  if (toe) {
    const ang = Math.atan2(vDot(toe, f.up), vDot(toe, f.right));
    s = rotSec(sec, ang);
  }
  if (axis === 'y') return prism(s, v3(0, 0, 0), v3(0, len, 0), { up: f.up });
  const yc = -secMinY(s);
  const a = vAdd(vMul(f.d, -len / 2), v3(0, yc, 0));
  const b = vAdd(vMul(f.d, len / 2), v3(0, yc, 0));
  return prism(s, a, b, { up: f.up });
}

/**
 * UPN-like channel (8-point C, b = 0.42 h, t = 0.12 h) of length `len` along
 * `axis`, toes pointing `toes` (default: +z for x/y members, +x for z). 28 tris.
 */
export function channel(
  len: number,
  h: number,
  o: { b?: number; t?: number; axis?: Axis; toes?: Toes } = {},
): Mesh {
  const axis = o.axis ?? 'x';
  const toes = o.toes ?? (axis === 'z' ? '+x' : '+z');
  return axisMember(secC(h, o.b ?? 0.42 * h, o.t ?? 0.12 * h), len, axis, TOE[toes]);
}

/**
 * HEB-like H beam (12 points; b = h, web 0.09 h, flanges 0.12 h — exaggerated
 * so the section reads at plant scale). `web` says which plan axis the web
 * runs along for a vertical member (default 'z'). 44 tris.
 */
export function hBeam(
  len: number,
  h: number,
  o: { b?: number; tw?: number; tf?: number; axis?: Axis; web?: 'x' | 'z' } = {},
): Mesh {
  const axis = o.axis ?? 'x';
  const sec = secH(h, o.b ?? h, o.tw ?? Math.max(0.25, 0.09 * h), o.tf ?? Math.max(0.3, 0.12 * h));
  if (axis === 'y') {
    // right = +x, up = +z: the section's web (local y) runs along z by default.
    const s = (o.web ?? 'z') === 'x' ? rotSec(sec, Math.PI / 2) : sec;
    return prism(s, v3(0, 0, 0), v3(0, len, 0), { up: v3(0, 0, 1) });
  }
  return axisMember(sec, len, axis, null);
}

/** Equal angle a × a × t (t = 0.15 a) along `axis`; `roll` rotates the section. 20 tris. */
export function angleBar(len: number, a: number, o: { t?: number; axis?: Axis; roll?: number } = {}): Mesh {
  return axisMember(rotSec(secL(a, o.t ?? 0.15 * a), o.roll ?? 0), len, o.axis ?? 'x', null);
}

export type SecKind = 'H' | 'C' | 'L' | 'SHS' | 'CHS' | 'FLAT';

function sectionOf(kind: SecKind, size: number): Vec2[] {
  switch (kind) {
    case 'H':
      return secH(size, size, Math.max(0.2, 0.09 * size), Math.max(0.25, 0.12 * size));
    case 'C':
      return secC(size, 0.42 * size, 0.12 * size);
    case 'L':
      return secL(size, 0.15 * size);
    case 'SHS':
      return secRect(size, size);
    case 'CHS':
      return secCircle(size / 2, 8);
    case 'FLAT':
      return secRect(size, Math.max(0.2, 0.25 * size));
  }
}

/**
 * Any section between two points — bracing, knee braces, stringers, spokes.
 * The section is centred on the a→b line; local right = (b − a) × up with up =
 * world +Y (world +Z for a vertical member); `roll` turns it about the line.
 * Tris: H 44, C 28, L 20, SHS 12, CHS 28, FLAT 12.
 */
export function member(a: Vec3, b: Vec3, sec: SecKind, size: number, o: { roll?: number; caps?: boolean } = {}): Mesh {
  if (Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z) < 1e-4) return emptyMesh();
  const s = rotSec(sectionOf(sec, size), o.roll ?? 0);
  return prism(s, a, b, { caps: o.caps ?? true, creaseDeg: sec === 'CHS' ? 50 : 35 });
}

/**
 * A base plate s × t × s on the floor (chamfered edges), optionally with hex
 * anchor nuts at the corners. 30 tris + 16 per bolt.
 */
export function basePlate(s: number, o: { t?: number; bolts?: 0 | 2 | 4 } = {}): Mesh {
  const t = o.t ?? 0.5;
  const parts: Mesh[] = [bevelBox(s, t, s, Math.min(0.15, t * 0.3))];
  const nb = o.bolts ?? 0;
  const off = 0.34 * s;
  const at: [number, number][] =
    nb === 4 ? [[off, off], [-off, off], [off, -off], [-off, -off]] : nb === 2 ? [[off, 0], [-off, 0]] : [];
  for (const [x, z] of at) parts.push(translate(hexPrism(0.16 * s, Math.max(0.25, 0.1 * s)), x, t, z));
  return merge(...parts);
}

/** A levelling foot: square pad + hex nut, `h` tall. 26 tris. */
function levelFoot(s: number, h: number): Mesh {
  return merge(
    extrude(ngon(s * 0.7, 4, Math.PI / 4), h * 0.35, { caps: 'top' }),
    translate(hexPrism(s * 0.7, h * 0.65), 0, h * 0.35, 0),
  );
}

/**
 * A welded machine base frame filling exactly w × h × d: perimeter channels
 * (toes inward), cross members at ≤ `cross` pitch (default 45) and levelling
 * feet housed WITHIN the frame height (nothing below y = 0).
 * Tris ≈ 4×28 + cross×28 + 4×26; 90 × 40 → ≈ 250.
 */
export function baseFrame(
  w: number,
  d: number,
  h: number,
  o: { section?: 'C' | 'H' | 'SHS'; cross?: number; feet?: boolean } = {},
): Mesh {
  const feet = o.feet ?? h >= 3;
  const fh = feet ? Math.min(h * 0.3, Math.max(0.6, h * 0.25)) : 0;
  const ch = h - fh;
  const sec = o.section ?? 'C';
  const b = sec === 'C' ? 0.42 * ch : ch;
  const parts: Mesh[] = [];
  const along = (len: number, axis: 'x' | 'z', toes: Toes) =>
    sec === 'C'
      ? channel(len, ch, { axis, toes })
      : sec === 'H'
        ? hBeam(len, ch, { axis })
        : axisMember(secRect(ch, ch), len, axis, null);
  for (const s of [-1, 1]) {
    parts.push(translate(along(w, 'x', s > 0 ? '-z' : '+z'), 0, fh, s * (d / 2 - b / 2)));
    parts.push(translate(along(Math.max(0.5, d - 2 * b), 'z', s > 0 ? '-x' : '+x'), s * (w / 2 - b / 2), fh, 0));
  }
  const pitch = o.cross ?? 45;
  const nC = Math.max(0, Math.ceil(w / pitch) - 1);
  for (let k = 1; k <= nC; k++) {
    const x = -w / 2 + (w * k) / (nC + 1);
    parts.push(translate(along(Math.max(0.5, d - 2 * b), 'z', '+x'), x, fh, 0));
  }
  if (feet)
    for (const sx of [-1, 1])
      for (const sz of [-1, 1]) parts.push(translate(levelFoot(Math.min(b, 2.4), fh), sx * (w / 2 - b / 2), 0, sz * (d / 2 - b / 2)));
  return merge(...parts);
}

/**
 * Four legs on a w × d stand of height h, on base plates, braced on the chosen
 * sides (n = −z, s = +z, e = +x, w = −x). Bracing is automatic when
 * h > 12 · size. Tris: SHS ≈ 216, H ≈ 344.
 */
export function legStand(
  w: number,
  d: number,
  h: number,
  o: {
    section?: 'H' | 'SHS' | 'CHS' | 'L';
    size?: number;
    inset?: number;
    brace?: 'x' | 'k' | 'h' | 'none';
    plates?: boolean;
    sides?: ('n' | 's' | 'e' | 'w')[];
  } = {},
): Mesh {
  const size = o.size ?? clampN(0.07 * Math.min(w, d), 2.2, 6);
  const sec = o.section ?? 'SHS';
  const plates = o.plates ?? true;
  const pt = plates ? 0.5 : 0;
  const hx = w / 2 - (o.inset ?? 0) - size / 2;
  const hz = d / 2 - (o.inset ?? 0) - size / 2;
  const parts: Mesh[] = [];
  const corners: [number, number][] = [
    [hx, hz],
    [-hx, hz],
    [hx, -hz],
    [-hx, -hz],
  ];
  for (const [x, z] of corners) {
    parts.push(member(v3(x, pt, z), v3(x, h, z), sec, size, { caps: true }));
    if (plates) parts.push(translate(basePlate(Math.min(2.2 * size, size + 3), { t: pt }), x, 0, z));
  }
  const brace = o.brace ?? (h > 12 * size ? 'h' : 'none');
  if (brace !== 'none') {
    const bs = Math.max(0.6, size * 0.5);
    const sides = o.sides ?? ['n', 's', 'e', 'w'];
    for (const sd of sides) {
      const [a, b] =
        sd === 'n' ? [v3(-hx, 0, -hz), v3(hx, 0, -hz)] : sd === 's' ? [v3(-hx, 0, hz), v3(hx, 0, hz)] : sd === 'e' ? [v3(hx, 0, -hz), v3(hx, 0, hz)] : [v3(-hx, 0, -hz), v3(-hx, 0, hz)];
      const at = (q: Vec3, y: number) => v3(q.x, y, q.z);
      if (brace === 'h') parts.push(member(at(a, h * 0.22), at(b, h * 0.22), 'SHS', bs));
      else if (brace === 'x') {
        parts.push(member(at(a, h * 0.12), at(b, h * 0.88), 'FLAT', bs * 1.4));
        parts.push(member(at(b, h * 0.12), at(a, h * 0.88), 'FLAT', bs * 1.4));
      } else {
        const mid = v3((a.x + b.x) / 2, h * 0.9, (a.z + b.z) / 2);
        parts.push(member(at(a, h * 0.15), mid, 'FLAT', bs * 1.4), member(at(b, h * 0.15), mid, 'FLAT', bs * 1.4));
      }
    }
  }
  return merge(...parts);
}

/**
 * A tubular vessel leg of height h: open tube (detail seg), a base plate and
 * triangular gussets. `orient` turns the gussets (radians about Y) so they run
 * radially on a ring of legs. Tris ≈ 2 · seg + 30 + 8 · gussets (16 seg → 78).
 */
export function pipeLeg(h: number, r: number, o: { gussets?: 0 | 2 | 3; plate?: boolean; orient?: number } = {}): Mesh {
  const seg = segFor(r, 'detail');
  const plate = o.plate ?? true;
  const pt = plate ? 0.5 : 0;
  const parts: Mesh[] = [translate(cylinder(r, Math.max(0.1, h - pt), { seg, capTop: false, capBottom: false }), 0, pt, 0)];
  const ext = Math.min(1.5, 0.5 * r);
  if (plate) parts.push(basePlate(2 * (r + ext), { t: pt }));
  const g = o.gussets ?? 2;
  if (g > 0) {
    const gl = Math.min(1.5, 0.6 * r);
    const gh = Math.min(h * 0.25, 2.2 * r);
    for (let k = 0; k < g; k++) {
      // Path along +z: local right = −x, so the section is drawn at −r…−(r+gl).
      const sec: Vec2[] = [
        { x: -r * 0.98, y: pt },
        { x: -(r + gl), y: pt },
        { x: -r * 0.98, y: pt + gh },
      ];
      const gm = prism(sec, v3(0, 0, -0.18), v3(0, 0, 0.18), { caps: true });
      rotateY(gm, (o.orient ?? 0) + (TAU * k) / g);
      parts.push(gm);
    }
  }
  return merge(...parts);
}

/**
 * The silo ring girder at the cone-to-barrel junction: a box-section ring
 * (bottom, outer wall, top — the inner face is against the shell) of depth h
 * at height y, outside radius R. The legs land on it. Tris 6 · seg.
 */
export function ringBeam(R: number, y: number, h: number, o: { seg?: number; width?: number } = {}): Mesh {
  const seg = o.seg ?? segFor(R, 'body');
  const wd = o.width ?? h * 0.9;
  return revolve(
    [
      { x: R, y },
      { x: R + wd, y, e: true },
      { x: R + wd, y: y + h, e: true },
      { x: R, y: y + h },
    ],
    seg,
    false,
  );
}

/**
 * A caged fixed ladder, stiles at x = ±w/2 in the z = 0 plane, climbing face
 * +z, the wall (and optional stand-off brackets) at −z. Rungs every
 * `rungPitch` (default 11 ≈ 275 mm), cage from `cageFrom` (default 0.3 h,
 * min 40) with hoops at ≤ `hoopPitch` (48) and 5 vertical straps.
 * Tris h = 90 ≈ 300.
 */
export function cagedLadder(
  h: number,
  o: { w?: number; rungPitch?: number; cageFrom?: number; hoopPitch?: number; cage?: boolean; stand?: number } = {},
): { rails: Mesh; rungs: Mesh; cage: Mesh } {
  const w = o.w ?? 5;
  const pitch = o.rungPitch ?? 11;
  const bar = Math.max(0.35, 0.07 * w);
  const sd = Math.max(0.8, 0.2 * w);
  const rails = merge(
    prism(secRect(bar, sd), v3(-w / 2, 0, 0), v3(-w / 2, h, 0), { up: v3(0, 0, 1) }),
    prism(secRect(bar, sd), v3(w / 2, 0, 0), v3(w / 2, h, 0), { up: v3(0, 0, 1) }),
  );
  const rr = Math.max(0.22, 0.05 * w);
  const rungL: Mesh[] = [];
  for (let y = pitch; y < h - 0.5; y += pitch) rungL.push(tube([v3(-w / 2, y, 0), v3(w / 2, y, 0)], rr, 6, false));
  const stand = o.stand ?? 0;
  const cageFrom = o.cageFrom ?? Math.max(40, 0.3 * h);
  const doCage = o.cage ?? (h >= 60 && cageFrom < h - 10);
  const cageL: Mesh[] = [];
  if (stand > 0.5)
    for (let y = 30; y < h; y += 60)
      for (const sx of [-1, 1]) cageL.push(member(v3((sx * w) / 2, y, -stand), v3((sx * w) / 2, y, 0), 'FLAT', sd * 0.8));
  if (doCage) {
    const rc = Math.max(0.8 * w, w / 2 + 1.5);
    const cz = 0.3 * rc;
    const arcPts = [180, 135, 90, 45, 0].map((dg) => {
      const t = (dg * Math.PI) / 180;
      return { x: rc * Math.cos(t), z: cz + rc * Math.sin(t) };
    });
    const hp = o.hoopPitch ?? 48;
    const n = Math.max(1, Math.ceil((h - cageFrom) / hp));
    const hsec = secRect(Math.max(0.25, 0.05 * w), Math.max(0.5, 0.12 * w));
    for (let k = 0; k <= n; k++) {
      const y = cageFrom + ((h - cageFrom) * k) / n;
      const path = [v3(-w / 2, y, 0), ...arcPts.map((q) => v3(q.x, y, q.z)), v3(w / 2, y, 0)];
      cageL.push(sweep(path, hsec, { caps: false, up: v3(0, 1, 0) }));
    }
    for (const q of arcPts) cageL.push(prism(secRect(Math.max(0.3, 0.06 * w), Math.max(0.3, 0.06 * w)), v3(q.x, cageFrom, q.z), v3(q.x, h, q.z), { up: v3(0, 0, 1) }));
  }
  return { rails, rungs: merge(...rungL), cage: merge(...cageL) };
}

/**
 * A guard rail along a floor-level poly-line (closed when the first and last
 * points coincide): square posts at ≤ `postPitch` (default 60), a top rail and
 * a knee rail (8-seg tubes, filleted corners) and a toe board. `h` defaults to
 * 44 (1.1 m, human scale; vessel access steel passes a vessel-scaled h).
 * Tris per 100 units ≈ 104.
 */
export function handrail(
  path: Vec3[],
  o: { h?: number; postPitch?: number; knee?: boolean; toe?: boolean } = {},
): { posts: Mesh; rails: Mesh; toe: Mesh } {
  const rh = o.h ?? 44;
  const pitch = o.postPitch ?? 60;
  const closed = path.length > 2 && Math.hypot(path[0].x - path[path.length - 1].x, path[0].y - path[path.length - 1].y, path[0].z - path[path.length - 1].z) < 1e-3;
  const pts = closed ? path.slice(0, -1) : path.slice();
  if (pts.length < 2) return { posts: emptyMesh(), rails: emptyMesh(), toe: emptyMesh() };
  const ps = Math.max(0.5, 0.036 * rh);
  const rr = Math.max(0.25, 0.018 * rh);
  const postL: Mesh[] = [];
  const nSeg = closed ? pts.length : pts.length - 1;
  const addPost = (q: Vec3, dir: Vec3) => {
    const pm2 = bevelBox(ps, Math.max(0.5, rh - rr), ps, Math.min(0.2, ps * 0.2));
    rotateY(pm2, -Math.atan2(dir.z, dir.x));
    postL.push(translate(pm2, q.x, q.y, q.z));
  };
  for (let i = 0; i < nSeg; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const dv = vSub(b, a);
    const L = Math.hypot(dv.x, dv.y, dv.z);
    const n = Math.max(1, Math.ceil(L / pitch));
    for (let k = 0; k < n; k++) addPost(vAdd(a, vMul(dv, k / n)), dv);
  }
  if (!closed) addPost(pts[pts.length - 1], vSub(pts[pts.length - 1], pts[pts.length - 2]));
  const rail = (y: number): Mesh => {
    const up = pts.map((q) => v3(q.x, q.y + y, q.z));
    if (closed) return sweep(up, secCircle(rr, 8), { closed: true, creaseDeg: 60 });
    return tube(filletArc(up, Math.min(4 * rr, 3), 3), rr, 8, true);
  };
  const railL: Mesh[] = [rail(rh - rr)];
  if (o.knee ?? true) railL.push(rail(rh * 0.5));
  let toe = emptyMesh();
  if (o.toe ?? true) {
    const th = Math.min(4, 0.1 * rh);
    const sec: Vec2[] = [
      { x: -0.15, y: 0 },
      { x: 0.15, y: 0 },
      { x: 0.15, y: th },
      { x: -0.15, y: th },
    ];
    toe = sweep(closed ? pts : pts, sec, { closed, caps: !closed, up: v3(0, 1, 0) });
  }
  return { posts: merge(...postL), rails: merge(...railL), toe };
}

const noRail = () => ({ posts: emptyMesh(), rails: emptyMesh(), toe: emptyMesh() });

/**
 * An annular access platform round a vessel of radius R at height y: grating
 * deck split into panels (joints featured, so it reads as grating), an edge
 * angle, knee brackets to the shell and a rail on the outer edge (vessel-scaled
 * h = clamp(0.09 y, 5, 44) unless `railH`). Tris R = 25, w = 8 ≈ 400.
 */
export function platformRing(
  R: number,
  y: number,
  width: number,
  o: { arc?: number; arcStart?: number; rail?: boolean; brackets?: number; railH?: number } = {},
): { deck: Mesh; frame: Mesh; rail: { posts: Mesh; rails: Mesh; toe: Mesh } } {
  const arc = o.arc ?? TAU;
  const a0 = o.arcStart ?? 0;
  const Ro = R + width;
  const t = Math.max(0.4, 0.06 * width);
  const full = Math.abs(arc - TAU) < 1e-6;
  const segAll = Math.max(4, Math.round((segFor(Ro, 'body') * arc) / TAU));
  const np = Math.max(1, Math.round((arc * Ro) / 30));
  const deckL: Mesh[] = [];
  for (let k = 0; k < np; k++) {
    const pa = a0 + (arc * k) / np;
    const s = Math.max(1, Math.round(segAll / np));
    deckL.push(revolve([{ x: Ro, y }, { x: R, y }], s, false, { arc: arc / np, arcStart: pa }));
  }
  const frameL: Mesh[] = [
    revolve(
      [
        { x: Ro - Math.min(1.5, width * 0.2), y: y - 3 * t },
        { x: Ro, y: y - 3 * t, e: true },
        { x: Ro, y, e: true },
      ],
      segAll,
      false,
      { arc, arcStart: a0 },
    ),
  ];
  const nb = o.brackets ?? Math.max(3, Math.round((arc * R) / 40));
  for (let k = 0; k < nb; k++) {
    const ang = a0 + (arc * (k + (full ? 0 : 0.5))) / nb;
    const c = Math.cos(ang);
    const s = Math.sin(ang);
    frameL.push(member(v3(R * c, y - width * 0.7, R * s), v3((R + width * 0.85) * c, y - t * 1.5, (R + width * 0.85) * s), 'L', Math.max(0.8, width * 0.12)));
  }
  let rail = noRail();
  if (o.rail ?? true) {
    const n = Math.max(3, Math.round(arc / (TAU / 24)));
    const rp: Vec3[] = [];
    const rr = Ro - 0.6;
    for (let k = 0; k <= n; k++) {
      const ang = a0 + (arc * k) / n;
      rp.push(v3(rr * Math.cos(ang), y, rr * Math.sin(ang)));
    }
    if (full) rp[rp.length - 1] = rp[0];
    rail = handrail(rp, { h: o.railH ?? clampN(0.09 * y, 5, 44), postPitch: 60 });
  }
  return { deck: merge(...deckL), frame: merge(...frameL), rail };
}

/**
 * A rectangular machine platform w × d with its deck top at y: grating panels
 * (≤ 40, joints featured), a channel edge (toes inward), rails on the closed
 * sides and optional H legs on base plates. Tris 120 × 60 ≈ 450.
 */
export function platformRect(
  w: number,
  d: number,
  y: number,
  o: { open?: ('n' | 's' | 'e' | 'w')[]; rail?: boolean; legs?: boolean; railH?: number } = {},
): { deck: Mesh; frame: Mesh; rail: { posts: Mesh; rails: Mesh; toe: Mesh } } {
  const nx = Math.max(1, Math.ceil(w / 40));
  const nz = Math.max(1, Math.ceil(d / 40));
  const deck = emptyMesh();
  for (let i = 0; i < nx; i++)
    for (let j = 0; j < nz; j++) {
      const x0 = -w / 2 + (w * i) / nx;
      const x1 = -w / 2 + (w * (i + 1)) / nx;
      const z0 = -d / 2 + (d * j) / nz;
      const z1 = -d / 2 + (d * (j + 1)) / nz;
      quad(deck, v3(x0, y, z1), v3(x1, y, z1), v3(x1, y, z0), v3(x0, y, z0), { n: v3(0, 1, 0), edges: 15 });
    }
  featurize(deck);
  const ch = clampN(0.07 * Math.min(w, d), 2.5, 7);
  const b = 0.42 * ch;
  const frameL: Mesh[] = [];
  for (const s of [-1, 1]) {
    frameL.push(translate(channel(w, ch, { axis: 'x', toes: s > 0 ? '-z' : '+z' }), 0, y - ch, s * (d / 2 - b / 2)));
    frameL.push(translate(channel(Math.max(0.5, d - 2 * b), ch, { axis: 'z', toes: s > 0 ? '-x' : '+x' }), s * (w / 2 - b / 2), y - ch, 0));
  }
  if (o.legs && y - ch > 1)
    for (const sx of [-1, 1])
      for (const sz of [-1, 1]) {
        const cs = Math.max(2.5, ch * 0.9);
        const x = sx * (w / 2 - cs / 2);
        const z = sz * (d / 2 - cs / 2);
        frameL.push(translate(hBeam(y - ch - 0.5, cs, { axis: 'y' }), x, 0.5, z), translate(basePlate(cs * 1.8, { bolts: 4 }), x, 0, z));
      }
  let rail = noRail();
  if (o.rail ?? true) {
    const open = new Set(o.open ?? []);
    const P = (x: number, z: number) => v3(x, y, z);
    const hx = w / 2 - 0.5;
    const hz = d / 2 - 0.5;
    const sides: Record<'n' | 's' | 'e' | 'w', [Vec3, Vec3]> = {
      n: [P(-hx, -hz), P(hx, -hz)],
      e: [P(hx, -hz), P(hx, hz)],
      s: [P(hx, hz), P(-hx, hz)],
      w: [P(-hx, hz), P(-hx, -hz)],
    };
    const posts: Mesh[] = [];
    const rails: Mesh[] = [];
    const toes: Mesh[] = [];
    for (const k of ['n', 'e', 's', 'w'] as const) {
      if (open.has(k)) continue;
      const r = handrail(sides[k], { h: o.railH ?? 44 });
      posts.push(r.posts);
      rails.push(r.rails);
      toes.push(r.toe);
    }
    rail = { posts: merge(...posts), rails: merge(...rails), toe: merge(...toes) };
  }
  return { deck, frame: merge(...frameL), rail };
}

/**
 * A stair flight: foot at x = 0 rising toward +x to height `rise` at
 * `pitchDeg` (default 45°). Channel stringers, grating treads with a nosing
 * bevel (going ≈ 10, rise ≈ 8), rails parallel to the pitch line.
 * Tris rise 100 ≈ 570.
 */
export function stairFlight(
  rise: number,
  o: { width?: number; pitchDeg?: number; rails?: 'both' | 'left' | 'right' | 'none' } = {},
): { stringers: Mesh; treads: Mesh; rail: { posts: Mesh; rails: Mesh; toe: Mesh } } {
  const width = o.width ?? 28;
  const th = ((o.pitchDeg ?? 45) * Math.PI) / 180;
  const run = rise / Math.tan(th);
  const n = Math.max(2, Math.round(rise / 8));
  const stepR = rise / n;
  const going = run / n;
  const sh = clampN(0.2 * width, 4, 8);
  const sb = 0.42 * sh;
  const zS = width / 2 - sb / 2;
  const s = sh / 2;
  const stringers: Mesh[] = [];
  for (const sz of [-1, 1]) {
    const a = v3(s * Math.sin(th), s * Math.cos(th), sz * zS);
    const b = v3(run, rise, sz * zS);
    // Local right is +z along a rising +x member; toes point inward.
    stringers.push(member(a, b, 'C', sh, { roll: sz > 0 ? Math.PI : 0 }));
  }
  const treads: Mesh[] = [];
  for (let k = 1; k < n; k++)
    treads.push(translate(bevelBox(going + 1, 0.8, width - 2 * sb, 0.3), (k - 0.5) * going, k * stepR - 0.8, 0));
  const railsSel = o.rails ?? 'both';
  let rail = noRail();
  if (railsSel !== 'none') {
    const posts: Mesh[] = [];
    const rails: Mesh[] = [];
    for (const sz of [-1, 1]) {
      if ((railsSel === 'left' && sz > 0) || (railsSel === 'right' && sz < 0)) continue;
      const r = handrail([v3(0, 0, sz * (width / 2 - 0.4)), v3(run, rise, sz * (width / 2 - 0.4))], { h: 36, toe: false, postPitch: 50 });
      posts.push(r.posts);
      rails.push(r.rails);
    }
    rail = { posts: merge(...posts), rails: merge(...rails), toe: emptyMesh() };
  }
  return { stringers: merge(...stringers), treads: merge(...treads), rail };
}

// ─── v1 structural exports (upgraded in place, v1 envelope) ───────────────

/** A concrete floor pad, slightly larger than the machine on it — chamfered edges. 30 tris. */
export function plinth(w: number, d: number, h = 2.5): Mesh {
  return bevelBox(w, h, d, Math.min(0.6, h * 0.3));
}

/**
 * Four SHS legs (2r square) at the corners of a `w × d` footprint on base
 * plates, with flat-bar bracing when h > 18. `inset` pulls them in from the
 * extremities (fraction of w, d). v1 envelope; plates add ≤ 1.5 in plan.
 * Tris ≈ 200.
 */
export function legs(w: number, d: number, h: number, r = 2.2, inset = 0.16): Mesh {
  const hx = w / 2 - w * inset;
  const hz = d / 2 - d * inset;
  const pt = Math.min(0.5, h * 0.05);
  const ext = Math.min(1.5, 0.5 * r);
  const parts: Mesh[] = [];
  for (const [x, z] of [
    [hx, hz],
    [-hx, hz],
    [hx, -hz],
    [-hx, -hz],
  ] as [number, number][]) {
    parts.push(member(v3(x, pt, z), v3(x, h, z), 'SHS', 2 * r));
    parts.push(translate(basePlate(2 * (r + ext), { t: pt }), x, 0, z));
  }
  if (h > 18)
    for (const sz of [-1, 1]) parts.push(member(v3(-hx, h * 0.22, sz * hz), v3(hx, h * 0.22, sz * hz), 'FLAT', Math.min(1.6, r * 0.9), { roll: Math.PI / 2 }));
  return merge(...parts);
}

/**
 * Legs arranged round a circular vessel — the classic silo support ring:
 * tubular legs with radial gussets on base plates, tied by an 8-seg hoop
 * through the leg centres. v1 envelope; plates add ≤ 1.5. Tris ≈ n × 78 + 64.
 */
export function ringLegs(R: number, h: number, n = 4, r = 2.4): Mesh {
  const parts: Mesh[] = [];
  const at: Vec3[] = [];
  for (let i = 0; i < n; i++) {
    const t = (TAU * i) / n + Math.PI / n;
    const x = Math.cos(t) * R;
    const z = Math.sin(t) * R;
    at.push(v3(x, h * 0.35, z));
    parts.push(translate(pipeLeg(h, r, { gussets: 2, orient: -t }), x, 0, z));
  }
  at.push(at[0]);
  parts.push(sweep(at, secCircle(Math.min(0.75, r * 0.4), 8), { closed: true, creaseDeg: 60 }));
  return merge(...parts);
}

/** A square-section beam between two points — bracing, rails, frame members. */
export function beam(a: Vec3, b: Vec3, t = 2): Mesh {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dy, dz);
  if (len < 1e-4) return emptyMesh();
  const m = box(t, len, t);
  // Aim +Y down the a→b direction.
  const yaw = Math.atan2(dx, dz);
  const pitch = Math.acos(Math.max(-1, Math.min(1, dy / len)));
  rotateX(m, pitch);
  rotateY(m, yaw);
  return translate(m, a.x, a.y, a.z);
}

/**
 * A welded skid: a perimeter channel frame exactly w × h × d with levelling
 * feet. No cross members — v1 had none, and one under the middle of a machine
 * would become the surface `dockPorts` snaps a bottom port onto. Tris ≈ 220.
 */
export function skid(w: number, d: number, h = 4): Mesh {
  return baseFrame(w, d, h, { feet: h >= 3, cross: Infinity });
}

/**
 * An annular access platform at height y round a vessel of radius R: ring
 * grating, edge band, toe plate and a rail — one mesh (callers draw it galv).
 * Stays inside the v1 envelope (radius R + width, y … y + 5.6), so the rail is
 * 5 tall; use `platformRing` for vessel-scaled access steel.
 */
export function platform(R: number, y: number, width = 8): Mesh {
  const outer = R + width;
  const seg = 18; // v1 segment count: keeps the v1 bounding box exactly.
  const t = 0.6;
  const parts: Mesh[] = [
    revolve(
      [
        { x: R, y },
        { x: outer, y, e: true },
        { x: outer, y: y + 2, e: true },
        { x: outer - 0.3, y: y + 2, e: true },
        { x: outer - 0.3, y: y + t },
        { x: R, y: y + t },
      ],
      seg,
      false,
    ),
  ];
  const rr = 0.3;
  const railR = outer - 0.5;
  const topY = y + 5.6 - rr;
  const rp: Vec3[] = [];
  for (let k = 0; k <= seg; k++) {
    const a = (TAU * k) / seg;
    rp.push(v3(railR * Math.cos(a), topY, railR * Math.sin(a)));
  }
  parts.push(sweep(rp, secRect(rr * 2, rr * 2), { closed: true }));
  const posts = 8;
  for (let k = 0; k < posts; k++) {
    const a = (TAU * (k + 0.5)) / posts;
    parts.push(translate(extrude(ngon(0.35, 4, a), topY - (y + t), { caps: 'none' }), railR * Math.cos(a), y + t, railR * Math.sin(a)));
  }
  return merge(...parts);
}

/**
 * A fixed ladder, stiles at x = ±w/2: chamfered square stiles and 6-seg rungs
 * at max(4, 0.6 w) pitch. Stays inside the v1 envelope (a flat ladder, z ±0.6),
 * so it has no cage — use `cagedLadder` for a real caged ladder.
 */
export function ladder(h: number, w = 5): Mesh {
  const parts: Mesh[] = [
    translate(bevelBox(1.2, h, 1.2, 0.15), -w / 2, 0, 0),
    translate(bevelBox(1.2, h, 1.2, 0.15), w / 2, 0, 0),
  ];
  const step = Math.max(4, 0.6 * w);
  for (let y = step; y < h; y += step) parts.push(tube([v3(-w / 2, y, 0), v3(w / 2, y, 0)], 0.55, 6, false));
  return merge(...parts);
}

// ─── Machine elements (§3.7) ──────────────────────────────────────────────

export interface MotorOpts {
  /** Overall length, drive-end face to fan-cowl end (the shaft is extra). */
  len: number;
  /** Radius at the rib tips. */
  r: number;
  /** 1: shaft out of the −X end; −1: out of the +X end. */
  dir?: 1 | -1;
  mount?: 'foot' | 'flange' | 'vertical';
  tb?: 'top' | 'side';
  detail?: 'hero' | 'std' | 'compact' | 'auto';
  mat?: ZoneMap;
  /** Shaft extension beyond the drive-end face (default 0.7 r). */
  shaft?: number;
  /** Underside of the feet below the axis (default 1.08 r). */
  footDrop?: number;
  /** Top of the terminal box above the axis (default 1.36 r). */
  tbTop?: number;
}

type MotorDetail = 'hero' | 'std' | 'compact';

function motorDetail(o: MotorOpts): MotorDetail {
  if (LOD === 1) return 'compact';
  const d = o.detail ?? 'auto';
  if (d !== 'auto') return d;
  return o.r < 5 ? 'compact' : o.r < 12 ? 'std' : 'hero';
}

/** The motor split by zone, axis along X at y = 0, shaft at −X, centred on the frame. */
function motorMeshes(o: MotorOpts): { ribs: Mesh; drive: Mesh[]; bright: Mesh[]; cavity: Mesh[]; product: Mesh[]; bezel: Mesh[] } {
  const L = o.len;
  const r = o.r;
  const det = motorDetail(o);
  const seg = det === 'hero' ? 16 : 12;
  const x0 = -L / 2;
  const es = 0.09 * L;
  const cl = 0.2 * L;
  const fx0 = x0 + es;
  const fx1 = L / 2 - cl;
  const shL = o.shaft ?? 0.7 * r;
  const shR = Math.max(0.15, 0.13 * r);
  const drop = o.footDrop ?? 1.08 * r;
  const tbTop = o.tbTop ?? 1.36 * r;
  const drive: Mesh[] = [];
  const bright: Mesh[] = [];
  const cavity: Mesh[] = [];
  const product: Mesh[] = [];
  const bezel: Mesh[] = [];
  const alongX = (prof: ProfilePt[], s = seg) => yTo(revolve(prof, s, false), 'x');

  // Frame: AXIAL cooling ribs (the single most recognisable TEFC cue).
  const sec: Vec2[] = [];
  if (det === 'hero') {
    const nR = 16;
    const st = TAU / nR;
    for (let k = 0; k < nR; k++) {
      const a = st * k;
      for (const [da, rad] of [
        [-0.22, r],
        [0.22, r],
        [0.5, 0.9 * r],
      ] as [number, number][])
        sec.push({ x: Math.cos(a + da * st) * rad, y: Math.sin(a + da * st) * rad });
    }
  } else {
    const nR = 12;
    for (let k = 0; k < 2 * nR; k++) {
      const a = (Math.PI * k) / nR;
      const rad = k % 2 === 0 ? r : 0.9 * r;
      sec.push({ x: Math.cos(a) * rad, y: Math.sin(a) * rad });
    }
  }
  const ribs = prism(sec, v3(fx0, 0, 0), v3(fx1, 0, 0), { caps: false, up: v3(0, 1, 0) });

  // Drive-end endshield (+ bearing boss above compact).
  if (det === 'compact')
    drive.push(alongX([{ x: 0, y: x0 }, { x: 0.94 * r, y: x0, e: true }, { x: 0.94 * r, y: fx0, e: true }]));
  else {
    const bl = Math.min(0.06 * L, 0.4 * r);
    const prof: ProfilePt[] = [
      { x: 0, y: x0 - bl },
      { x: 0.32 * r, y: x0 - bl, e: true },
      { x: 0.32 * r, y: x0, e: true },
    ];
    if (det === 'hero') prof.push({ x: 0.8 * r, y: x0, e: true });
    prof.push({ x: 0.94 * r, y: fx0, e: true });
    drive.push(alongX(prof));
  }
  // Fan cowl with the grille face.
  const cc = Math.min(0.04 * L, 0.12 * r);
  if (det === 'compact') {
    drive.push(alongX([{ x: 0.88 * r, y: fx1 }, { x: 0.97 * r, y: fx1, e: true }, { x: 0.97 * r, y: L / 2, e: true }]));
    cavity.push(decal(alongX([{ x: 0.97 * r, y: L / 2 }, { x: 0, y: L / 2 }])));
  } else {
    const prof: ProfilePt[] = [
      { x: 0.88 * r, y: fx1 },
      { x: 0.97 * r, y: fx1, e: true },
      { x: 0.97 * r, y: L / 2 - cc, e: true },
    ];
    if (det === 'hero') prof.push({ x: 0.88 * r, y: L / 2, e: true });
    prof.push({ x: 0.6 * r, y: L / 2, e: true });
    drive.push(alongX(prof));
    cavity.push(decal(alongX([{ x: 0.6 * r, y: L / 2 }, { x: 0, y: L / 2 }])));
  }
  // Shaft (+ key above compact).
  const sseg = det === 'compact' ? 6 : 8;
  bright.push(alongX([{ x: 0, y: x0 - shL }, { x: shR, y: x0 - shL, e: true }, { x: shR, y: x0 }], sseg));
  if (det !== 'compact' && shL > 1) bright.push(translate(box(shL * 0.55, shR * 0.28, shR * 0.5), x0 - shL * 0.5, shR * 0.82, 0));

  // Terminal box, 0.35 of the frame from the drive end.
  const tbx = fx0 + 0.35 * (fx1 - fx0);
  const tbW = Math.min(0.9 * r, 0.4 * (fx1 - fx0));
  const tbY0 = 0.86 * r;
  const tbH = Math.max(0.2 * r, tbTop - tbY0);
  const tbD = 0.8 * r;
  const tbM =
    det === 'compact' ? box(tbW, tbH, tbD) : roundBox(tbW, tbH, tbD, Math.min(tbW, tbD) * 0.14, { seg: 1, top: 'bevel' });
  const tbSide = (o.tb ?? 'top') === 'side';
  if (tbSide) {
    // +Y (the box's height) onto +Z: the box stands off the +Z flank.
    rotateX(tbM, Math.PI / 2);
    translate(tbM, tbx, 0, tbY0);
  } else translate(tbM, tbx, tbY0, 0);
  drive.push(tbM);
  if (det === 'hero' && !tbSide) {
    const gl = rotateX(cylinder(0.12 * r, 0.25 * r, { seg: 6, capBottom: false }), Math.PI / 2);
    bezel.push(translate(gl, tbx, tbY0 + tbH * 0.4, tbD / 2 - 0.05));
  }
  // Mounting.
  const mount = o.mount ?? 'foot';
  if (mount === 'foot') {
    const fl = Math.max(1, 0.8 * (fx1 - fx0));
    const fw = 0.22 * r;
    for (const sz of [-1, 1]) {
      const f = det === 'hero' ? bevelBox(fl, drop - 0.62 * r, fw, Math.min(0.3, fw * 0.2), { bottom: true }) : box(fl, drop - 0.62 * r, fw);
      drive.push(translate(f, (fx0 + fx1) / 2, -drop, sz * 0.62 * r));
    }
  } else {
    // B5 flange ring, set a little behind the drive-end face so the two
    // faces are never coplanar.
    const ft = Math.max(0.3, 0.05 * L);
    const fx = x0 + 0.12 * es;
    drive.push(
      alongX([
        { x: 0.5 * r, y: fx },
        { x: 1.12 * r, y: fx, e: true },
        { x: 1.12 * r, y: fx + ft, e: true },
        { x: 0.94 * r, y: fx + ft, e: true },
      ]),
    );
  }
  if (det === 'hero') {
    // Lifting eye on the frame top, and the stainless rating plate.
    const er = 0.14 * r;
    const ring6: Vec3[] = [];
    for (let k = 0; k < 6; k++) {
      const a = (TAU * k) / 6;
      ring6.push(v3((fx0 + fx1) / 2 + er * Math.cos(a), r + er * 1.1 + er * Math.sin(a), 0));
    }
    ring6.push(ring6[0]);
    drive.push(sweep(ring6, secRect(er * 0.35, er * 0.35), { closed: true }));
    product.push(namePlateMesh(v3((fx0 + fx1) / 2 + 0.1 * (fx1 - fx0), 0, -r * 1.0), v3(0, 0, -1), 0.35 * (fx1 - fx0), 0.3 * r));
  }
  return { ribs, drive, bright, cavity, product, bezel };
}

function orientMotor(meshes: Mesh[], o: MotorOpts): void {
  const mount = o.mount ?? 'foot';
  for (const m of meshes) {
    if (mount === 'vertical') rotateZ(m, Math.PI / 2);
    else if ((o.dir ?? 1) === -1) rotateY(m, Math.PI);
  }
}

/**
 * A TEFC induction motor along X, shaft out of the −X end (×`dir`), axis at
 * y = 0, centred on its length (drive-end face at x = −len/2). `r` is the rib
 * tip radius; feet reach y = −1.08 r (place the axis at 1.08 r to stand it on
 * the floor). Axial ribs, drive-end endshield with bearing boss, fan cowl with
 * a dark grille, terminal box at 0.35 of the frame, feet or a B5 flange;
 * 'vertical' stands it shaft-down. 'auto' detail: compact r < 5, std < 12,
 * hero above. Zones: frame/cowl/TB → drive, shaft → bright, grille → cavity,
 * plate → product, gland → bezel.
 * Tris: compact ≈ 200, std ≈ 280, hero ≈ 560.
 */
export function tefcMotor(o: MotorOpts): Part[] {
  const m = motorMeshes(o);
  const all = [m.ribs, ...m.drive, ...m.bright, ...m.cavity, ...m.product, ...m.bezel];
  orientMotor(all, o);
  return assy(
    { mesh: merge(m.ribs, ...m.drive), mat: zm('drive', o.mat) },
    m.bright.length > 0 && { mesh: merge(...m.bright), mat: zm('bright', o.mat) },
    m.cavity.length > 0 && { mesh: merge(...m.cavity), mat: zm('cavity', o.mat) },
    m.product.length > 0 && { mesh: merge(...m.product), mat: zm('product', o.mat) },
    m.bezel.length > 0 && { mesh: merge(...m.bezel), mat: zm('bezel', o.mat) },
  );
}

/**
 * v1 drive motor lying along +X (shaft out of the −X end; `dir` = −1 flips
 * it): now a TEFC motor (`tefcMotor`, detail 'auto') inside the v1 envelope —
 * X from −0.8 len to +0.57 len, rib tips 1.05 r, feet to −1.1 r, terminal box
 * to 1.41 r. `fins` is the ribbed frame, `body` everything else; both are
 * single meshes for the caller's material. Tris ≈ 200–300.
 */
export function motorUnit(len: number, r: number, dir: 1 | -1 = 1): { body: Mesh; fins: Mesh } {
  const o: MotorOpts = { len: 1.13 * len, r: 1.05 * r, shaft: 0.24 * len, footDrop: 1.1 * r, tbTop: 1.41 * r };
  const m = motorMeshes(o);
  const body = merge(...m.drive, ...m.bright, ...m.cavity, ...m.product, ...m.bezel);
  const out = { body: translate(body, 0.005 * len, 0, 0), fins: translate(m.ribs, 0.005 * len, 0, 0) };
  if (dir === -1) {
    rotateY(out.body, Math.PI);
    rotateY(out.fins, Math.PI);
  }
  return out;
}

export type MotorKind = 'extruderMain' | 'pump' | 'feeder' | 'conveyor' | 'blower' | 'agitator' | 'haulOff';

/**
 * Standard drive proportions (§7.2), so every family sizes motors the same way.
 * `scale` is: extruderMain → barrel R (r = 2 R); pump → volute r (0.85);
 * feeder → equipment w (0.13); conveyor → equipment w (0.1); blower → casing
 * half-size (0.75); agitator → vessel R (0.3); haulOff → equipment w (0.09).
 * len = 3.6 r (endshield to cowl). Approximate IEC practice.
 */
export function motorSize(scale: number, kind: MotorKind): { len: number; r: number } {
  const k: Record<MotorKind, number> = {
    extruderMain: 2.0,
    pump: 0.85,
    feeder: 0.13,
    conveyor: 0.1,
    blower: 0.75,
    agitator: 0.3,
    haulOff: 0.09,
  };
  const r = Math.max(1.2, scale * k[kind]);
  return { r, len: 3.6 * r };
}

export interface GearOpts {
  w: number;
  h: number;
  d: number;
  /** helical: coaxial; parallel: output lower; bevel: output turned 90° (+Z); worm: input low and across. */
  kind?: 'helical' | 'bevel' | 'parallel' | 'worm';
  /** Input face: 'x' = −X face, 'z' = −Z face (default 'x'; worm 'z'). */
  inAxis?: 'x' | 'z';
  /** Output face: 'x' = +X, 'z' = +Z, 'y' = bottom (a vertical drive; the boss tip rests on y = 0). */
  outAxis?: 'x' | 'y' | 'z';
  mat?: ZoneMap;
  detail?: 'std' | 'compact' | 'auto';
}

interface GearLayout {
  kind: NonNullable<GearOpts['kind']>;
  inAxis: 'x' | 'z';
  outAxis: 'x' | 'y' | 'z';
  inY: number;
  outY: number;
  compact: boolean;
}

function gearLayout(o: GearOpts): GearLayout {
  const kind = o.kind ?? 'helical';
  const inAxis = o.inAxis ?? (kind === 'worm' ? 'z' : 'x');
  const outAxis = o.outAxis ?? (kind === 'bevel' ? 'z' : 'x');
  const fr: Record<typeof kind, [number, number]> = {
    helical: [0.55, 0.55],
    parallel: [0.68, 0.4],
    bevel: [0.55, 0.5],
    worm: [0.3, 0.62],
  };
  const det = o.detail ?? 'auto';
  const compact = LOD === 1 || det === 'compact' || (det === 'auto' && Math.min(o.w, o.h, o.d) < 8);
  const lift = outAxis === 'y' ? Math.min(0.12 * o.h, 0.25 * Math.min(o.w, o.d)) : 0;
  return { kind, inAxis, outAxis, inY: lift + fr[kind][0] * (o.h - lift), outY: outAxis === 'y' ? 0 : fr[kind][1] * o.h, compact };
}

/**
 * A cast reduction gearbox filling w × h × d (bosses included): rounded
 * housing, stiffening ribs, input boss with seal cap, output boss with a
 * flange, foot pads, breather (to 1.12 h), drain plug. Zones: housing → drive,
 * plug → fastener. Tris: std ≈ 240, compact ≈ 100.
 */
export function gearHousing(o: GearOpts): Part[] {
  const L = gearLayout(o);
  const { w, h, d } = o;
  const mn = Math.min(h, d);
  const rIn = Math.min(0.16 * mn, 0.3 * w);
  const rOut = Math.min(0.22 * mn, 0.35 * w);
  const bl = Math.min(0.12 * w, 0.14 * d, 0.5 * rOut + 0.6);
  // The housing shrinks on each face that carries a boss.
  let x0 = -w / 2;
  let x1 = w / 2;
  let z0 = -d / 2;
  let z1 = d / 2;
  if (L.inAxis === 'x') x0 += bl;
  else z0 += bl;
  if (L.outAxis === 'x') x1 -= bl;
  else if (L.outAxis === 'z') z1 -= bl;
  const ribP = L.compact ? 0 : Math.min(0.04 * d, 0.8);
  if (!L.compact) {
    if (L.inAxis !== 'z') z0 += ribP;
    if (L.outAxis !== 'z') z1 -= ribP;
  }
  const y0 = L.outAxis === 'y' ? Math.min(0.12 * h, 0.25 * Math.min(w, d)) : 0;
  const hw = x1 - x0;
  const hd = z1 - z0;
  const hh = h - y0;
  const cx = (x0 + x1) / 2;
  const cz = (z0 + z1) / 2;
  const drive: Mesh[] = [];
  const fast: Mesh[] = [];
  const housing = L.compact
    ? bevelBox(hw, hh, hd, Math.min(0.6, 0.06 * Math.min(hw, hh, hd)))
    : roundBox(hw, hh, hd, 0.12 * Math.min(hw, hd), { seg: 2, top: 'round' });
  drive.push(translate(housing, cx, y0, cz));
  if (!L.compact && ribP > 0.05) {
    const rt = Math.max(0.3, 0.04 * hw);
    const faces: number[] = [];
    if (L.inAxis !== 'z') faces.push(-1);
    if (L.outAxis !== 'z') faces.push(1);
    for (const sz of faces)
      for (const fx of [0]) {
        // Outer face exactly on the envelope, inner face buried 0.05 in the housing.
        const zc = sz < 0 ? z0 - ribP / 2 + 0.025 : z1 + ribP / 2 - 0.025;
        drive.push(translate(extrude(rectPoly(rt, ribP + 0.05), hh * 0.8, { caps: 'top' }), cx + fx * hw, y0 + hh * 0.08, zc));
      }
  }
  const segIn = segFor(rIn, 'detail');
  const segOut = segFor(rOut, 'detail');
  // Bosses are built along +Y from 0 to their length, then aimed at their face.
  const inBoss = revolve([{ x: rIn, y: 0 }, { x: rIn, y: bl, e: true }, { x: 0, y: bl }], segIn, false);
  if (L.inAxis === 'x') translate(rotateZ(inBoss, Math.PI / 2), x0, L.inY, cz);
  else translate(rotateX(inBoss, -Math.PI / 2), cx, L.inY, z0);
  drive.push(inBoss);
  // A bottom (vertical) output fills exactly the lift under the housing, so
  // its flange face is the floor-level datum.
  const ol = L.outAxis === 'y' ? y0 : bl;
  const outBoss = revolve(
    L.compact || ol < 0.4
      ? [{ x: rOut, y: 0 }, { x: rOut, y: ol, e: true }, { x: 0, y: ol }]
      : [
          { x: rOut, y: 0 },
          { x: rOut, y: ol * 0.55, e: true },
          { x: 1.3 * rOut, y: ol * 0.55, e: true },
          { x: 1.3 * rOut, y: ol, e: true },
          { x: 0, y: ol },
        ],
    segOut,
    false,
  );
  if (L.outAxis === 'x') translate(rotateZ(outBoss, -Math.PI / 2), x1, L.outY, cz);
  else if (L.outAxis === 'z') translate(rotateX(outBoss, Math.PI / 2), cx, L.outY, z1);
  else translate(rotateX(outBoss, Math.PI), cx, y0, cz);
  drive.push(outBoss);
  if (!L.compact && L.outAxis !== 'y') {
    const pl = hw * 0.84;
    const pd = Math.min(0.14 * d, 2.5);
    for (const zz of [-d / 2 + pd / 2, d / 2 - pd / 2]) drive.push(translate(extrude(rectPoly(pl, pd), Math.max(0.3, 0.07 * h), { caps: 'top' }), cx, 0, zz));
  }
  // Breather: v1 stood it on top to 1.12 h — kept, so the envelope is unchanged.
  const br = Math.max(0.2, 0.05 * Math.min(w, d));
  const bx = cx + 0.22 * hw;
  drive.push(translate(cylinder(br, 0.12 * h, { seg: 6, capBottom: false }), bx, h, cz));
  if (!L.compact) {
    const plug = rotateZ(hexPrism(Math.max(0.4, 0.07 * hh), 0.3), Math.PI / 2);
    fast.push(translate(plug, x0 + 0.01, y0 + 0.14 * hh, cz + 0.25 * hd));
  }
  return assy({ mesh: merge(...drive), mat: zm('drive', o.mat) }, fast.length > 0 && { mesh: merge(...fast), mat: zm('fastener', o.mat) });
}

/**
 * v1 reduction gearbox — now a cast helical `gearHousing` in ONE mesh (the
 * caller's material, usually 'cast'), inside the v1 envelope w × 1.12 h × d.
 * Tris ≈ 100 (small) – 240.
 */
export function gearbox(w: number, h: number, d: number): Mesh {
  return merge(...gearHousing({ w, h, d, kind: 'helical' }).map((q) => q.mesh));
}

/**
 * A sheet-metal coupling guard over a shaft on the X axis (axis at y = 0):
 * half-round top of radius r on skirts down to −`drop` (default r), end plates
 * with dark shaft notches. 'full' is a closed tube guard. Zone: guard.
 * Tris ≈ 40 (+ 6 per slot).
 */
export function couplingGuard(
  len: number,
  r: number,
  o: { style?: 'half' | 'full'; slots?: boolean; axis?: 'x' | 'z'; drop?: number; mat?: ZoneMap } = {},
): Part[] {
  const n = Math.max(4, segFor(r, 'detail') / 2);
  const drop = o.drop ?? r;
  const sec: Vec2[] = [];
  if (o.style === 'full') for (let k = 0; k < 2 * n; k++) sec.push({ x: r * Math.cos((Math.PI * k) / n), y: r * Math.sin((Math.PI * k) / n) });
  else {
    sec.push({ x: r, y: -drop });
    for (let k = 0; k <= n; k++) sec.push({ x: r * Math.cos((Math.PI * k) / n), y: r * Math.sin((Math.PI * k) / n) });
    sec.push({ x: -r, y: -drop });
  }
  const g = prism(sec, v3(-len / 2, 0, 0), v3(len / 2, 0, 0), { caps: true, up: v3(0, 1, 0) });
  const cav: Mesh[] = [];
  const sr = Math.min(0.4 * r, drop * 0.9);
  for (const sx of [-1, 1]) {
    const fan = revolve([{ x: sr, y: 0 }, { x: 0, y: 0 }], 8, false);
    // Built facing +Y; stand it up as a half-disc facing ±X at the end plate.
    rotateX(fan, -Math.PI / 2);
    rotateY(fan, sx > 0 ? -Math.PI / 2 : Math.PI / 2);
    cav.push(decal(translate(fan, sx * (len / 2 + 0.06), 0, 0)));
  }
  if (o.slots) {
    for (let k = -1; k <= 1; k++)
      cav.push(decal(plateQuad(v3((k * len) / 4, r + 0.08, 0), v3(len * 0.08, 0, 0), v3(0, 0, r * 0.45), v3(0, 1, 0))));
  }
  const parts = assy({ mesh: g, mat: zm('guard', o.mat) }, cav.length > 0 && { mesh: merge(...cav), mat: zm('cavity', o.mat) });
  if (o.axis === 'z') turnA(parts, 'y', -Math.PI / 2);
  return parts;
}

/**
 * A cast bell housing / motor lantern from radius r0 (y = 0) to r1 (y = len)
 * with end flanges and two dark inspection windows. Along +Y by default
 * (vertical drives: mixers, agitators, crammers). Zone: drive. Tris ≈ 100.
 */
export function lantern(len: number, r0: number, r1: number, o: { windows?: boolean; axis?: Axis; mat?: ZoneMap } = {}): Part[] {
  const seg = segFor(Math.max(r0, r1), 'body');
  const ft = Math.max(0.3, 0.1 * len);
  const body = revolve(
    [
      { x: r0 * 1.15, y: 0 },
      { x: r0 * 1.15, y: ft, e: true },
      { x: r0, y: ft, e: true },
      { x: r1, y: len - ft, e: true },
      { x: r1 * 1.15, y: len - ft, e: true },
      { x: r1 * 1.15, y: len, e: true },
      { x: 0, y: len },
    ],
    seg,
    false,
  );
  const cav: Mesh[] = [];
  if (o.windows ?? true) {
    const rm = (r0 + r1) / 2;
    for (const sz of [-1, 1])
      cav.push(decal(plateQuad(v3(0, len / 2, sz * (rm + 0.25)), v3(rm * 0.35, 0, 0), v3(0, len * 0.22, 0), v3(0, 0, sz))));
  }
  const parts = assy({ mesh: body, mat: zm('drive', o.mat) }, cav.length > 0 && { mesh: merge(...cav), mat: zm('cavity', o.mat) });
  const axis = o.axis ?? 'y';
  if (axis !== 'y') for (const q of parts) yTo(q.mesh, axis);
  return parts;
}

/**
 * Motor → coupling guard → gearbox on ONE shaft line, so every extruder, pump
 * and conveyor drive looks like the same factory. `at` is where the drive's
 * output enters the driven machine (the gearbox output face, or the motor
 * shaft if there is no gearbox); the train extends along +axis × dir from it,
 * and shaft heights are resolved automatically. `pedestal` adds machine-dark
 * stools under the motor and gearbox down to the floor (at.y below).
 */
export function driveTrain(o: {
  at: Vec3;
  axis: 'x' | 'z';
  dir: 1 | -1;
  motor: MotorOpts;
  gear?: GearOpts;
  guard?: boolean;
  pedestal?: boolean;
}): Part[] {
  const parts: Part[] = [];
  let cursor = 0;
  let shaftY = 0;
  const floor = -o.at.y;
  const stool = (x0: number, x1: number, top: number, d: number) => {
    const hgt = top - floor;
    if (!o.pedestal || hgt < 0.5) return;
    parts.push({ mesh: translate(bevelBox(Math.max(0.5, x1 - x0), hgt, d, Math.min(0.4, 0.05 * d)), (x0 + x1) / 2, floor, 0), mat: zm('base') });
  };
  if (o.gear) {
    const g = o.gear;
    const L = gearLayout(g);
    const gp = gearHousing({ ...g, inAxis: 'x', outAxis: g.outAxis === 'z' || g.outAxis === 'y' ? g.outAxis : 'x' });
    // gearHousing takes input on −X and output on +X; the train needs the
    // output facing −X (into the machine), so turn it round.
    turnA(gp, 'y', Math.PI);
    moveA(gp, g.w / 2, -L.outY, 0);
    parts.push(...gp);
    stool(0, g.w, -L.outY, g.d * 0.9);
    cursor = g.w;
    shaftY = L.inY - L.outY;
  }
  const m = o.motor;
  const guard = o.guard ?? (!!o.gear && (m.mount ?? 'foot') === 'foot');
  if (guard) {
    const gl = Math.max(2, 0.6 * m.r);
    parts.push(...moveA(couplingGuard(gl, 0.55 * m.r, { drop: Math.min(0.55 * m.r, 1.08 * m.r - 0.3) }), cursor + gl / 2, shaftY, 0));
    cursor += gl;
  }
  const mo: MotorOpts = { ...m, dir: 1, shaft: guard ? (m.shaft ?? 0.7 * m.r) : 0.05 };
  const mp = tefcMotor(mo);
  moveA(mp, cursor + m.len / 2, shaftY, 0);
  parts.push(...mp);
  stool(cursor + 0.1 * m.len, cursor + 0.9 * m.len, shaftY - (m.footDrop ?? 1.08 * m.r), 1.5 * m.r);
  const out = assy(parts);
  if (o.dir === -1) turnA(out, 'y', Math.PI);
  if (o.axis === 'z') turnA(out, 'y', -Math.PI / 2);
  return moveA(out, o.at.x, o.at.y, o.at.z);
}

/**
 * An SN-type split plummer block for a shaft of radius `shaftR` along X
 * (default), base on the floor, shaft centre at 2.4 shaftR: base with bolt
 * lugs, half-round cap (split line featured), grease nipple. Zones: cast,
 * fastener. Tris ≈ 110.
 */
export function plummerBlock(shaftR: number, o: { axis?: 'x' | 'z'; mat?: ZoneMap } = {}): Part[] {
  const H = 2.4 * shaftR;
  const Lz = 7.5 * shaftR;
  const B = 2.4 * shaftR;
  const bh = 0.9 * shaftR;
  const rh = 1.6 * shaftR;
  const cast: Mesh[] = [bevelBox(B, bh, Lz, Math.min(0.3, 0.08 * B))];
  cast.push(translate(openBox(B * 0.9, H - bh, 2 * rh), 0, bh, 0));
  const cap = frustum(rh, rh, B * 0.9, { seg: 12, arc: Math.PI, arcStart: 0, capTop: true, capBottom: true });
  // Built along +Y as a half-shell over +X; lay it along X with the round side up.
  rotateZ(cap, -Math.PI / 2);
  rotateX(cap, -Math.PI / 2);
  cast.push(translate(cap, -B * 0.45, H, 0));
  cast.push(translate(cylinder(0.18 * shaftR, 0.5 * shaftR, { seg: 6, capBottom: false }), 0, H + rh, 0));
  const fast: Mesh[] = [];
  for (const sz of [-1, 1]) fast.push(translate(hexPrism(0.5 * shaftR, 0.3 * shaftR), 0, bh, sz * 0.38 * Lz));
  const parts = assy({ mesh: merge(...cast), mat: zm('cast', o.mat) }, { mesh: merge(...fast), mat: zm('fastener', o.mat) });
  if (o.axis === 'z') turnA(parts, 'y', Math.PI / 2);
  return parts;
}

/**
 * A V-groove sheave of radius r and width w, axis along X (default) centred on
 * the origin, `grooves` (default 2) grooves. Tris ≈ 2 · seg · (2g + 3).
 */
export function pulley(r: number, w: number, o: { grooves?: number; axis?: 'x' | 'z' } = {}): Mesh {
  const g = Math.max(1, Math.round(o.grooves ?? 2));
  const seg = segFor(r, 'body');
  const sl = 0.08 * w;
  const gw = (w - 2 * sl) / g;
  const prof: ProfilePt[] = [{ x: 0, y: -w / 2 }, { x: r, y: -w / 2, e: true }];
  for (let k = 0; k < g; k++) {
    const y0 = -w / 2 + sl + k * gw;
    prof.push({ x: r, y: y0, e: true }, { x: 0.78 * r, y: y0 + gw / 2, e: true });
  }
  prof.push({ x: r, y: w / 2 - sl, e: true }, { x: r, y: w / 2, e: true }, { x: 0, y: w / 2 });
  return yTo(revolve(prof, seg, false), o.axis ?? 'x');
}

/**
 * A V-belt guard: the hull of two circles (r1 at the origin, r2 at +c along
 * local X) in the XY plane, extruded `w` along the shaft axis (Z by default)
 * with chamfered edges — the classic Roots-blower or conveyor drive guard.
 * Zone: guard. Tris ≈ 110.
 */
export function beltGuard(c: number, r1: number, r2: number, w: number, o: { axis?: 'x' | 'z'; slots?: boolean; mat?: ZoneMap } = {}): Part[] {
  const sinP = Math.max(-0.95, Math.min(0.95, (r1 - r2) / Math.max(c, 1e-3)));
  const al = Math.PI / 2 - Math.asin(sinP);
  const out: Vec2[] = [];
  const n2 = 6;
  const n1 = 8;
  for (let k = 0; k <= n2; k++) {
    const a = -al + (2 * al * k) / n2;
    out.push({ x: c + r2 * Math.cos(a), y: r2 * Math.sin(a) });
  }
  for (let k = 0; k <= n1; k++) {
    const a = al + ((TAU - 2 * al) * k) / n1;
    out.push({ x: r1 * Math.cos(a), y: r1 * Math.sin(a) });
  }
  const g = slab(out, w, Math.min(0.3 * w, 0.12 * Math.min(r1, r2)));
  const cav: Mesh[] = [];
  if (o.slots) for (const sx of [0.3, 0.5, 0.7]) cav.push(decal(plateQuad(v3(c * sx, 0, w / 2 + 0.06), v3(c * 0.04, 0, 0), v3(0, Math.min(r1, r2) * 0.5, 0), v3(0, 0, 1))));
  const parts = assy({ mesh: g, mat: zm('guard', o.mat) }, cav.length > 0 && { mesh: merge(...cav), mat: zm('cavity', o.mat) });
  if (o.axis === 'x') turnA(parts, 'y', Math.PI / 2);
  return parts;
}

/**
 * A roller of radius r and length `len` along Z (default) centred on the
 * origin: shell with chamfered ends, dark recessed end discs, shaft stubs and
 * optional plummer blocks. Default material `bright` (chrome godets, chill
 * rolls); pass mat 'machine' for idlers. Tris ≈ 240 (seg 24).
 */
export function roller(
  r: number,
  len: number,
  o: { axis?: 'x' | 'z'; ends?: 'hub' | 'flat' | 'chamfer'; shaft?: number; bearings?: boolean; mat?: MatKey; seg?: number } = {},
): Part[] {
  const seg = o.seg ?? segFor(r, r >= 3 ? 'hero' : 'body');
  const ends = o.ends ?? 'chamfer';
  const lip = ends === 'flat' ? 0 : clampN(0.05 * r, 0.25, 1.2);
  const hl = len / 2;
  const shell = revolve(
    lip > 0
      ? [
          { x: r - lip, y: -hl, e: true },
          { x: r, y: -hl + lip, e: true },
          { x: r, y: hl - lip, e: true },
          { x: r - lip, y: hl, e: true },
        ]
      : [
          { x: r, y: -hl, e: true },
          { x: r, y: hl, e: true },
        ],
    seg,
    false,
  );
  const endR = r - lip;
  const cav = merge(
    revolve([{ x: 0, y: -hl }, { x: endR, y: -hl }], seg, false),
    revolve([{ x: endR, y: hl }, { x: 0, y: hl }], seg, false),
  );
  const sr = o.shaft ?? Math.max(0.2, 0.18 * r);
  const sl = Math.max(0.6, 0.12 * len);
  const stubs = merge(
    revolve([{ x: 0, y: -hl - sl }, { x: sr, y: -hl - sl, e: true }, { x: sr, y: -hl }], 8, false),
    revolve([{ x: sr, y: hl }, { x: sr, y: hl + sl, e: true }, { x: 0, y: hl + sl }], 8, false),
  );
  const parts: Part[] = [
    { mesh: shell, mat: o.mat ?? zm('bright') },
    { mesh: cav, mat: zm('cavity') },
    { mesh: stubs, mat: zm('trim') },
  ];
  const axis = o.axis ?? 'z';
  for (const q of parts) yTo(q.mesh, axis);
  if (o.bearings) {
    const H = 2.4 * sr;
    for (const s of [-1, 1]) {
      const pb = plummerBlock(sr, { axis });
      const off = s * (hl + sl * 0.6);
      parts.push(...moveA(pb, axis === 'x' ? off : 0, -H, axis === 'z' ? off : 0));
    }
  }
  return assy(parts);
}

/**
 * v1 bank of idler rollers across +X, each lying along Z (length `width`),
 * centred on 0 — now chamfered, end-capped rollers at detail seg, one mesh.
 * v1 envelope. Tris ≈ n × 64.
 */
export function rollerBank(r: number, width: number, len: number, n: number): Mesh {
  const seg = segFor(r, 'detail');
  const lip = Math.min(0.08 * r, 0.2 * width);
  const one = () =>
    yTo(
      revolve(
        [
          { x: 0, y: -width / 2 },
          { x: r * 0.92, y: -width / 2, e: true },
          { x: r, y: -width / 2 + lip, e: true },
          { x: r, y: width / 2 - lip, e: true },
          { x: r * 0.92, y: width / 2, e: true },
          { x: 0, y: width / 2 },
        ],
        seg,
        false,
      ),
      'z',
    );
  const parts: Mesh[] = [];
  for (let i = 0; i < n; i++) parts.push(translate(one(), -len / 2 + (len * (i + 0.5)) / n, 0, 0));
  return merge(...parts);
}

/**
 * A single-groove cable sheave of radius r, width w, axis along Z (default)
 * — accumulators, dancers, capstan guides. Dark spoke windows at r ≥ 8.
 * Tris 10 · seg.
 */
export function sheave(r: number, w: number, o: { axis?: 'x' | 'z'; spokes?: boolean } = {}): Mesh {
  const seg = segFor(r, 'hero');
  const t = 0.18 * w;
  const m = revolve(
    [
      { x: 0, y: -w / 2 },
      { x: r, y: -w / 2, e: true },
      { x: r, y: -w / 2 + t, e: true },
      { x: 0.8 * r, y: 0, e: true },
      { x: r, y: w / 2 - t, e: true },
      { x: r, y: w / 2, e: true },
      { x: 0, y: w / 2 },
    ],
    seg,
    false,
  );
  const parts: Mesh[] = [m];
  if ((o.spokes ?? r >= 8) && LOD === 0)
    for (let k = 0; k < 4; k++) {
      const a = (TAU * k) / 4 + Math.PI / 4;
      for (const sy of [-1, 1]) {
        const c = v3(Math.cos(a) * 0.5 * r, sy * (w / 2 + 0.05), Math.sin(a) * 0.5 * r);
        parts.push(decal(plateQuad(c, v3(Math.cos(a) * 0.14 * r, 0, Math.sin(a) * 0.14 * r), v3(-Math.sin(a) * 0.08 * r, 0, Math.cos(a) * 0.08 * r), v3(0, sy, 0))));
      }
    }
  return yTo(merge(...parts), o.axis ?? 'z');
}

/**
 * A flanged cable drum, flange diameter D, overall width W, axis along Z
 * (default) at y = 0: two chamfered flanges (dark arbor bore, lightening
 * holes at D ≥ 40), barrel (visible when the reel is empty) and the wound
 * cable layer (`wound` = its diameter, default 0.8 D; 0 = empty).
 * Timber style adds battens. Zones: flanges trim/timber, cable rubber
 * (`mat.rubber` → 'cable' for a coloured jacket), bore cavity.
 * Tris ≈ 400 (seg 32).
 */
export function cableReel(
  D: number,
  W: number,
  o: { core?: number; wound?: number; style?: 'steel' | 'timber'; axis?: 'x' | 'z'; mat?: ZoneMap } = {},
): Part[] {
  const R = D / 2;
  const seg = segFor(R, 'hero');
  const ft = Math.max(0.5, 0.06 * W);
  const hw = W / 2;
  const core = (o.core ?? 0.5 * D) / 2;
  const wound = (o.wound ?? 0.8 * D) / 2;
  const ch = Math.min(0.4 * ft, 0.03 * R);
  const fl = (s: 1 | -1): Mesh => {
    // Flange from the inner face (|y| = hw − ft) to the outer face (|y| = hw).
    const yi = s * (hw - ft);
    const yo = s * hw;
    const rin = Math.max(core, wound) * 0.98;
    const prof: ProfilePt[] =
      s > 0
        ? [
            { x: rin, y: yi },
            { x: R - ch, y: yi, e: true },
            { x: R, y: yi + ch, e: true },
            { x: R, y: yo - ch, e: true },
            { x: R - ch, y: yo, e: true },
            { x: 0.12 * R, y: yo, e: true },
          ]
        : [
            { x: 0.12 * R, y: yo },
            { x: R - ch, y: yo, e: true },
            { x: R, y: yo + ch, e: true },
            { x: R, y: yi - ch, e: true },
            { x: R - ch, y: yi, e: true },
            { x: rin, y: yi, e: true },
          ];
    return revolve(prof, seg, false);
  };
  const flanges = merge(fl(1), fl(-1));
  const cav: Mesh[] = [revolve([{ x: 0.12 * R, y: hw }, { x: 0, y: hw }], 12, false), revolve([{ x: 0, y: -hw }, { x: 0.12 * R, y: -hw }], 12, false)];
  if (D >= 40 && LOD === 0)
    for (const sy of [-1, 1])
      for (let k = 0; k < 6; k++) {
        const a = (TAU * k) / 6 + Math.PI / 6;
        const c = v3(Math.cos(a) * 0.62 * R, sy * (hw + 0.06), Math.sin(a) * 0.62 * R);
        cav.push(plateQuad(c, v3(Math.cos(a) * 0.1 * R, 0, Math.sin(a) * 0.1 * R), v3(-Math.sin(a) * 0.07 * R, 0, Math.cos(a) * 0.07 * R), v3(0, sy, 0)));
      }
  const inner = hw - ft;
  const parts: Part[] = [
    { mesh: flanges, mat: o.style === 'timber' ? 'timber' : zm('trim', o.mat) },
    { mesh: decal(merge(...cav)), mat: zm('cavity', o.mat) },
  ];
  if (wound > core + 0.2) parts.push({ mesh: soft(revolve([{ x: wound, y: -inner }, { x: wound, y: inner }], seg, false)), mat: zm('rubber', o.mat) });
  else parts.push({ mesh: revolve([{ x: core, y: -inner }, { x: core, y: inner }], seg, false), mat: zm('trim', o.mat) });
  if (o.style === 'timber') {
    // Radial battens bolted across each outer face (a fresh mesh per batten).
    const bat: Mesh[] = [];
    for (const sy of [1, -1])
      for (let k = 0; k < 4; k++) {
        const b = translate(box(0.1 * R, 0.4, 0.78 * R), 0, 0, 0.55 * R);
        rotateY(b, (TAU * k) / 4 + Math.PI / 4);
        if (sy < 0) rotateX(b, Math.PI);
        bat.push(translate(b, 0, sy * hw, 0));
      }
    parts.push({ mesh: merge(...bat), mat: 'timber' });
  }
  const axis = o.axis ?? 'z';
  for (const q of parts) yTo(q.mesh, axis);
  return assy(parts);
}

/**
 * A caterpillar track in the XY plane centred on the origin (length `len`
 * along X, drum radius r, `width` along Z): hollow rubber belt loop, inner
 * side plates. Zones: rubber, trim. Tris ≈ 180.
 */
export function beltTrack(len: number, r: number, width: number, o: { idlers?: number; mat?: ZoneMap } = {}): Part[] {
  const belt = soft(stadium(len, r, width, { seg: 8, inner: true }));
  const pr = r * 0.8;
  const cx = Math.max(0, len / 2 - r);
  const out: Vec2[] = [];
  for (let j = 0; j <= 8; j++) {
    const t = -Math.PI / 2 + (Math.PI * j) / 8;
    out.push({ x: cx + Math.cos(t) * pr, y: Math.sin(t) * pr });
  }
  for (let j = 0; j <= 8; j++) {
    const t = Math.PI / 2 + (Math.PI * j) / 8;
    out.push({ x: -cx + Math.cos(t) * pr, y: Math.sin(t) * pr });
  }
  const plates = emptyMesh();
  const T = triangulate(out);
  for (const z of [-width / 2 + 0.1, width / 2 - 0.1])
    for (let t = 0; t < T.length; t += 3)
      tri(plates, v3(out[T[t]].x, out[T[t]].y, z), v3(out[T[t + 1]].x, out[T[t + 1]].y, z), v3(out[T[t + 2]].x, out[T[t + 2]].y, z), { n: v3(0, 0, Math.sign(z)) });
  featurize(plates);
  const hubs: Mesh[] = [];
  const nI = o.idlers ?? 0;
  for (let k = 0; k < nI; k++) {
    const x = -cx + (2 * cx * (k + 1)) / (nI + 1);
    for (const sz of [-1, 1]) hubs.push(translate(rotateX(cylinder(r * 0.25, 0.3, { seg: 8, capBottom: false }), (sz * Math.PI) / 2), x, -r * 0.55, sz * (width / 2 - 0.1)));
  }
  return assy({ mesh: belt, mat: zm('rubber', o.mat) }, { mesh: merge(plates, ...hubs), mat: zm('trim', o.mat) });
}

/**
 * A centrifugal scroll casing about the Z axis (impeller axis at the origin):
 * a spiral outline growing from 0.72 r at the tongue to r, grown into a
 * tangential outlet duct rising to 1.2 r (`outlet: 'up'`) or running out
 * sideways to +X (`'side'`), extruded `width` along Z with chamfered edges.
 * `hand` = −1 mirrors it. The suction nozzle is added with `nozzleRF`.
 * Tris ≈ 125.
 */
export function volute(r: number, width: number, o: { outlet?: 'up' | 'side'; hand?: 1 | -1; axis?: 'x' | 'z' } = {}): Mesh {
  const R1 = r;
  const r0 = 0.72 * r;
  const n = 12;
  const pts: Vec2[] = [];
  const top = 1.2 * r;
  const end = 1.8 * Math.PI;
  // Duct outer wall top → spiral clockwise from angle 0 → tongue → duct inner wall.
  pts.push({ x: R1, y: top });
  for (let k = 0; k <= n; k++) {
    const t = (end * k) / n;
    const rad = R1 - (R1 - r0) * (t / end);
    pts.push({ x: rad * Math.cos(-t), y: rad * Math.sin(-t) });
  }
  const tongue = pts[pts.length - 1];
  pts.push({ x: tongue.x, y: top });
  let outline = pts;
  if (o.outlet === 'side') outline = pts.map((q) => ({ x: q.y, y: -q.x }));
  if ((o.hand ?? 1) === -1) outline = outline.map((q) => ({ x: -q.x, y: q.y })).reverse();
  const m = slab(outline, width, Math.min(0.25 * width, 0.08 * r));
  if (o.axis === 'x') rotateY(m, Math.PI / 2);
  return m;
}

/**
 * A chiller / condenser fan facing +Y (radius r, origin at the shroud base):
 * shroud ring with a bevelled lip, a dark blade disc with 5 blade decals and a
 * guard of 3 concentric rings and 4 radials. Zones: skin (shroud), cavity,
 * trim (blades, guard). Tris ≈ 150.
 */
export function axialFan(r: number, o: { guard?: boolean; up?: boolean; mat?: ZoneMap } = {}): Part[] {
  const seg = segFor(r, 'body');
  const hgt = Math.max(0.8, 0.12 * r);
  const shroud = revolve(
    [
      { x: 1.08 * r, y: 0 },
      { x: 1.08 * r, y: hgt, e: true },
      { x: 1.18 * r, y: hgt + 0.1 * r, e: true },
      { x: r, y: hgt + 0.1 * r, e: true },
    ],
    seg,
    false,
  );
  const disc = decal(revolve([{ x: r, y: hgt * 0.5 }, { x: 0, y: hgt * 0.5 }], seg, false));
  const trim: Mesh[] = [];
  for (let k = 0; k < 5; k++) {
    const a = (TAU * k) / 5;
    const c = v3(Math.cos(a) * 0.55 * r, hgt * 0.5 + 0.1, Math.sin(a) * 0.55 * r);
    trim.push(decal(plateQuad(c, v3(Math.cos(a) * 0.38 * r, 0, Math.sin(a) * 0.38 * r), v3(-Math.sin(a + 0.4) * 0.14 * r, 0, Math.cos(a + 0.4) * 0.14 * r), v3(0, 1, 0))));
  }
  if (o.guard ?? true) {
    const gy = hgt + 0.1 * r + 0.15;
    const gs = Math.max(8, seg * 0.75 - ((seg * 0.75) % 4));
    for (const f of [0.35, 0.68, 0.98]) trim.push(translate(ring(f * r - Math.max(0.12, 0.02 * r), f * r, { seg: gs }), 0, gy, 0));
    for (let k = 0; k < 4; k++) {
      const a = (TAU * k) / 4;
      trim.push(plateQuad(v3(Math.cos(a) * 0.5 * r, gy + 0.02, Math.sin(a) * 0.5 * r), v3(Math.cos(a) * 0.5 * r, 0, Math.sin(a) * 0.5 * r), v3(-Math.sin(a) * 0.03 * r, 0, Math.cos(a) * 0.03 * r), v3(0, 1, 0)));
    }
  }
  const parts = assy({ mesh: shroud, mat: zm('skin', o.mat) }, { mesh: disc, mat: zm('cavity', o.mat) }, { mesh: merge(...trim), mat: zm('trim', o.mat) });
  if (o.up === false) turnA(parts, 'x', Math.PI);
  return parts;
}

/**
 * A flexible connection (LIW feeder, above and below the weigh hopper): a
 * zigzag revolve along +Y (default) from 0 to len, soft except the end rows.
 * Tris 4 · folds · seg.
 */
export function bellows(r: number, len: number, o: { folds?: number; axis?: Axis; seg?: number } = {}): Mesh {
  const folds = Math.max(1, Math.round(o.folds ?? 4));
  const seg = o.seg ?? segFor(r, 'detail');
  const prof: ProfilePt[] = [];
  const nP = 2 * folds;
  for (let k = 0; k <= nP; k++) prof.push({ x: k % 2 === 0 ? 0.86 * r : r, y: (len * k) / nP, e: k === 0 || k === nP });
  // The folds are dense detail (no lines); only the two end rims are drawn.
  const m = markRowsY(soft(revolve(prof, seg, false)), [0, len]);
  return yTo(m, o.axis ?? 'y');
}

/**
 * A shear-beam load cell on its mounting plate (s = plate side), cable gland
 * out of +X — the LIW-feeder identity. Zones: trim (plate), alu (cell),
 * bezel (gland). Tris ≈ 58.
 */
export function loadCell(s: number, o: { mat?: ZoneMap } = {}): Part[] {
  const t = Math.max(0.3, 0.12 * s);
  const plate = extrude(rectPoly(s, s * 0.7), t, { caps: 'top' });
  const cell = translate(bevelBox(s * 0.8, 0.35 * s, s * 0.3, 0.05 * s), 0, t, 0);
  const gl = translate(rotateZ(cylinder(0.08 * s, 0.25 * s, { seg: 6, capBottom: false }), -Math.PI / 2), s * 0.4, t + 0.17 * s, 0);
  return assy({ mesh: plate, mat: zm('trim', o.mat) }, { mesh: cell, mat: zm('alu', o.mat) }, { mesh: gl, mat: zm('bezel', o.mat) });
}

export interface BarrelOpts {
  /** Overall length along X (centred on 0), flange face to flange face. */
  len: number;
  /** Barrel radius (half the section height; the heater shell radius). */
  R: number;
  /** Twin-screw figure-8 core and rectangular shells. */
  twin?: boolean;
  sections: number;
  heat?: 'shell' | 'band' | 'none';
  /** Section indices with a vent port on top. */
  vents?: number[];
  /** Section index of the feed throat (top opening). */
  feedAt?: number;
  /** Section indices with a side-feeder port on +Z. */
  sideFeed?: number[];
  /** BUSS clamshell: hinge line on −Z, clamp lugs on +Z. */
  split?: boolean;
  /** Rows of kneading-pin heads on each section. */
  pins?: number;
  /** Hex bolts on the section flanges (default: R ≥ 10). */
  bolts?: boolean;
  /** Water manifold + drops under the barrel (default true). */
  manifold?: boolean;
  mat?: ZoneMap;
}

/**
 * The modular extruder barrel along +X, centred on 0, axis at y = 0: one
 * continuous core (twin figure-8 / single round), per-section alu heater
 * shells (split line featured on twin), square section flanges with chamfered
 * corners, thermocouple stubs, water manifold with drops, and flanged feed /
 * vent / side-feed ports. `split` adds the co-kneader hinge and clamp lugs,
 * `pins` rows of kneading-pin heads. It never builds the screw — add a short
 * `helicoid` visible through the feed throat. Section flanges reach
 * ±1.2 R (twin ±1.75 R in Z); single ±1.25 R.
 * Zones: core/flanges hotSteel, shells alu, TC bright, manifold product,
 * drops rubber, ports trim, pins fastener.
 * Tris: 8 twin ≈ 1,300 · 6 single ≈ 900 · 5 split + 3 pin rows ≈ 1,150.
 */
export function extruderBarrel(o: BarrelOpts): Part[] {
  const { len, R } = o;
  const n = Math.max(1, Math.round(o.sections));
  const twin = !!o.twin;
  const pitch = len / n;
  const ft = clampN(0.18 * R, 0.8, 3);
  const heat = o.heat ?? 'shell';
  const hot: Mesh[] = [];
  const alu: Mesh[] = [];
  const bright: Mesh[] = [];
  const prod: Mesh[] = [];
  const rub: Mesh[] = [];
  const trim: Mesh[] = [];
  const fast: Mesh[] = [];
  const cav: Mesh[] = [];
  // Core.
  const core: Vec2[] = [];
  if (twin) {
    const rc = 0.8 * R;
    const cz = 0.55 * R;
    const a0 = Math.acos(cz / rc);
    // Right lobe (centre +cz) from the upper cusp round the outside, then the left lobe.
    // Right lobe CCW from the lower cusp to the upper, then the left lobe on
    // round — both counter-clockwise, so the outline never crosses itself.
    for (let k = 0; k < 12; k++) {
      const a = -Math.PI + a0 + ((TAU - 2 * a0) * (k + 0.5)) / 12;
      core.push({ x: cz + rc * Math.cos(a), y: rc * Math.sin(a) });
    }
    for (let k = 0; k < 12; k++) {
      const a = a0 + ((TAU - 2 * a0) * (k + 0.5)) / 12;
      core.push({ x: -cz + rc * Math.cos(a), y: rc * Math.sin(a) });
    }
  } else for (let k = 0; k < 20; k++) core.push({ x: 0.85 * R * Math.cos((TAU * k) / 20), y: 0.85 * R * Math.sin((TAU * k) / 20) });
  hot.push(prism(core, v3(-len / 2 + ft / 2, 0, 0), v3(len / 2 - ft / 2, 0, 0), { caps: false, up: v3(0, 1, 0) }));
  // Section flanges (square, chamfered corners).
  const fy = twin ? 1.2 * R : 1.25 * R;
  const fz = twin ? 1.75 * R : 1.25 * R;
  const fsec = secChamferRect(2 * fz, 2 * fy, 0.22 * Math.min(fy, fz));
  const doBolts = (o.bolts ?? R >= 10) && LOD === 0;
  for (let k = 0; k <= n; k++) {
    let xa = -len / 2 + k * pitch - ft / 2;
    if (k === 0) xa = -len / 2;
    if (k === n) xa = len / 2 - ft;
    hot.push(prism(fsec, v3(xa, 0, 0), v3(xa + ft, 0, 0), { caps: true, up: v3(0, 1, 0) }));
    if (doBolts)
      for (const sx of k === 0 ? [1] : k === n ? [-1] : [-1, 1])
        for (const [by, bz] of [
          [0.78 * fy, 0.8 * fz],
          [-0.78 * fy, 0.8 * fz],
          [0.78 * fy, -0.8 * fz],
          [-0.78 * fy, -0.8 * fz],
        ] as [number, number][]) {
          const b = rotateZ(hexPrism(0.18 * R, 0.1 * R), (-sx * Math.PI) / 2);
          fast.push(translate(b, sx > 0 ? xa + ft : xa, by, bz));
        }
  }
  // Heater shells.
  const shellY = R;
  const shellZ = twin ? 1.55 * R : R;
  // Twin shells carry a vertex at mid-height on each side: the split line of
  // the two heater halves, authored as a feature column.
  const splitSec = (z: number, y: number, c: number): Vec2[] => [
    { x: -z + c, y: -y },
    { x: z - c, y: -y },
    { x: z, y: -y + c },
    { x: z, y: 0 },
    { x: z, y: y - c },
    { x: z - c, y },
    { x: -z + c, y },
    { x: -z, y: y - c },
    { x: -z, y: 0 },
    { x: -z, y: -y + c },
  ];
  const ssec = splitSec(shellZ, shellY, 0.3 * R);
  const ch = Math.min(0.25 * R, 0.08 * pitch);
  for (let k = 0; k < n; k++) {
    const xs0 = -len / 2 + k * pitch + ft / 2 + (k === 0 ? ft / 2 : 0) + 0.15;
    const xs1 = -len / 2 + (k + 1) * pitch - ft / 2 - (k === n - 1 ? ft / 2 : 0) - 0.15;
    if (heat === 'none' || xs1 - xs0 < 1) continue;
    const spans: [number, number][] =
      heat === 'band'
        ? [
            [xs0 + (xs1 - xs0) * 0.08, xs0 + (xs1 - xs0) * 0.44],
            [xs0 + (xs1 - xs0) * 0.56, xs0 + (xs1 - xs0) * 0.92],
          ]
        : [[xs0, xs1]];
    for (const [a, b] of spans) {
      const c2 = Math.min(ch, (b - a) * 0.3);
      if (twin) {
        const inset = splitSec(shellZ - c2, shellY - c2, Math.max(0.05, 0.3 * R - 0.4 * c2));
        const m = loftRows([secAtX(inset, a), secAtX(ssec, a + c2), secAtX(ssec, b - c2), secAtX(inset, b)], {
          axis: 'x',
          featRows: [0, 1, 2, 3],
          featCols: [3, 8],
        });
        capX(m, inset, a, -1);
        capX(m, inset, b, 1);
        alu.push(featurize(m));
      } else
        alu.push(
          yTo(
            revolve(
              [
                { x: 0.85 * R, y: a, e: true },
                { x: R, y: a + c2, e: true },
                { x: R, y: b - c2, e: true },
                { x: 0.85 * R, y: b, e: true },
              ],
              16,
              false,
            ),
            'x',
          ),
        );
    }
    // Thermocouple stub on top of every heated section.
    bright.push(translate(extrude(ngon(Math.max(0.35, 0.07 * R), 4, Math.PI / 4), Math.max(1, 0.25 * R), { caps: 'top' }), (xs0 + xs1) / 2 + pitch * 0.18, shellY - 0.1, 0));
  }
  // Ports.
  const cxOf = (k: number) => -len / 2 + (k + 0.5) * pitch;
  if (o.feedAt !== undefined && o.feedAt >= 0 && o.feedAt < n) {
    const pw = Math.min(0.62 * pitch, 2.2 * R);
    const pd = twin ? 1.8 * R : 1.5 * R;
    trim.push(translate(bevelBox(pw, 0.35 * R, pd, 0.08 * R), cxOf(o.feedAt), shellY, 0));
    cav.push(decal(plateQuad(v3(cxOf(o.feedAt), shellY + 0.35 * R + 0.06, 0), v3(pw * 0.36, 0, 0), v3(0, 0, pd * 0.34), v3(0, 1, 0))));
  }
  for (const k of o.vents ?? []) {
    if (k < 0 || k >= n) continue;
    const vr = Math.max(0.8, 0.32 * R);
    trim.push(nozzle(v3(cxOf(k), shellY - 0.2, 0), v3(0, 1, 0), vr, Math.max(2.5, 0.6 * R)));
  }
  for (const k of o.sideFeed ?? []) {
    if (k < 0 || k >= n) continue;
    const pw = Math.min(0.55 * pitch, 1.8 * R);
    const sp = rotateX(bevelBox(pw, 0.3 * R, 1.4 * R, 0.08 * R), Math.PI / 2);
    trim.push(translate(sp, cxOf(k), 0, shellZ - 0.1));
  }
  // Water manifold + drops (behind the barrel, +Z).
  if (o.manifold ?? true) {
    const my = -fy * 1.12;
    const mz = shellZ * 0.7;
    const mr = Math.max(0.3, 0.1 * R);
    prod.push(tube([v3(-len / 2 + ft, my, mz), v3(len / 2 - ft, my, mz)], mr, 8, true));
    for (let k = 0; k < n; k++) rub.push(tube([v3(cxOf(k) - pitch * 0.2, my, mz), v3(cxOf(k) - pitch * 0.2, -shellY * 0.9, mz * 0.9)], mr * 0.6, 6, false));
  }
  // Co-kneader clamshell.
  if (o.split) {
    trim.push(tube([v3(-len / 2 + ft, 0, -shellZ - 0.4 * R), v3(len / 2 - ft, 0, -shellZ - 0.4 * R)], 0.28 * R, 8, true));
    for (let k = 0; k < n; k++)
      for (const f of [0.3, 0.7]) trim.push(translate(openBox(0.12 * pitch, 0.5 * R, 0.45 * R), -len / 2 + (k + f) * pitch, -0.25 * R, shellZ + 0.18 * R));
  }
  // Kneading-pin heads.
  const rows = Math.max(0, Math.round(o.pins ?? 0));
  for (let k = 0; k < n && rows > 0; k++)
    for (let j = 0; j < rows; j++) {
      const ang = rows === 1 ? Math.PI / 2 : Math.PI * (0.25 + (0.5 * j) / (rows - 1));
      for (const f of [0.35, 0.65]) {
        const pn = v3(0, Math.sin(ang), -Math.cos(ang));
        const head = extrude(ngon(Math.max(0.35, 0.1 * R), 4, Math.PI / 4), Math.max(0.5, 0.14 * R), { caps: 'top' });
        fast.push(aim(head, v3(-len / 2 + (k + f) * pitch, shellY * pn.y * 0.96, shellZ * pn.z * 0.96), pn));
      }
    }
  return assy(
    { mesh: merge(...hot), mat: zm('hotSteel', o.mat) },
    alu.length > 0 && { mesh: merge(...alu), mat: zm('alu', o.mat) },
    bright.length > 0 && { mesh: merge(...bright), mat: zm('bright', o.mat) },
    prod.length > 0 && { mesh: merge(...prod), mat: zm('product', o.mat) },
    rub.length > 0 && { mesh: merge(...rub), mat: zm('rubber', o.mat) },
    trim.length > 0 && { mesh: merge(...trim), mat: zm('trim', o.mat) },
    fast.length > 0 && { mesh: merge(...fast), mat: zm('fastener', o.mat) },
    cav.length > 0 && { mesh: merge(...cav), mat: zm('cavity', o.mat) },
  );
}

/**
 * A feed hopper, outlet (0.28 r) at y = 0, top rim at h: conical (or
 * pyramidal with `square`) bottom to 0.45 h, straight wall, rolled rim. Open
 * hoppers show a dark interior; `lid` closes it with a hinged lid, `window`
 * adds a slot sight glass on the operator side (−Z), `level` a level probe.
 * Zones: product, glass, trim, cavity. Tris ≈ 220.
 */
export function feedHopper(o: { r: number; h: number; square?: boolean; lid?: boolean; window?: boolean; level?: boolean; mat?: ZoneMap }): Part[] {
  const { r, h } = o;
  const prod: Mesh[] = [];
  const trim: Mesh[] = [];
  const cav: Mesh[] = [];
  const glass: Mesh[] = [];
  const rimT = Math.max(0.4, 0.03 * r);
  if (o.square) {
    const row = (s: number, y: number) => secAtX(secRect(2 * s, 2 * s), 0).map((q) => v3(q.z, y, q.y));
    const rows = [row(0.28 * r, 0), row(r, 0.45 * h), row(r, h - rimT), row(r * 1.05, h - rimT * 0.2)];
    const m = loftRows(rows, { axis: 'y', featRows: [0, 1, 2, 3] });
    prod.push(m);
  } else {
    const seg = segFor(r, 'body');
    prod.push(
      revolve(
        [
          { x: 0.28 * r, y: 0, e: true },
          { x: r, y: 0.45 * h, e: true },
          { x: r, y: h - rimT, e: true },
          { x: 1.05 * r, y: h - rimT * 0.2, e: true },
          { x: 0.98 * r, y: h, e: true },
        ],
        seg,
        false,
      ),
    );
  }
  const seg = o.square ? 4 : segFor(r, 'body');
  if (o.lid) {
    const lr = o.square ? r * 1.06 : 1.06 * r;
    const lid = o.square ? bevelBox(2 * lr, 0.5, 2 * lr, 0.15) : cylinder(lr, 0.5, { seg, capBottom: false });
    trim.push(translate(lid, 0, h, 0));
    trim.push(translate(cylinderX(Math.max(0.3, 0.05 * r), 0.3 * r, 6), 0, h + 0.3, lr));
  } else cav.push(decal(o.square ? plateQuad(v3(0, h - rimT * 0.5, 0), v3(0.98 * r, 0, 0), v3(0, 0, 0.98 * r), v3(0, 1, 0)) : revolve([{ x: 0.99 * r, y: h - rimT * 0.5 }, { x: 0, y: h - rimT * 0.5 }], seg, false)));
  if (o.window) {
    const sg = sightGlass({ at: v3(0, 0.72 * h, -r - 0.05), n: v3(0, 0, -1), r: Math.max(0.6, 0.06 * r), style: 'slot', len: 0.4 * h });
    for (const q of sg) (q.mat === 'glass' ? glass : trim).push(q.mesh);
  }
  if (o.level) {
    trim.push(translate(cylinder(Math.max(0.3, 0.04 * r), 0.25 * h, { seg: 6, capBottom: false }), 0.6 * r, h - 0.1 * h, 0));
    trim.push(translate(box(0.16 * r, 0.1 * r, 0.16 * r), 0.6 * r, h + 0.15 * h, 0));
  }
  return assy(
    { mesh: merge(...prod), mat: zm('product', o.mat) },
    trim.length > 0 && { mesh: merge(...trim), mat: zm('trim', o.mat) },
    cav.length > 0 && { mesh: merge(...cav), mat: zm('cavity', o.mat) },
    glass.length > 0 && { mesh: merge(...glass), mat: zm('glass', o.mat) },
  );
}

/**
 * v1 heater bands round a barrel of radius r and length `len` along +X,
 * centred on 0 — each band now chamfered down to the barrel (radius 1.16 r, as v1).
 * v1 envelope. Tris ≈ n × 6 · seg (16 seg → 96).
 */
export function heaterBands(r: number, len: number, n: number): Mesh {
  const parts: Mesh[] = [];
  const t = Math.min(len / n / 2.4, r * 1.1);
  const seg = segFor(r, 'body');
  const c = Math.min(0.2 * t, 0.08 * r);
  for (let i = 0; i < n; i++) {
    const x = -len / 2 + (len * (i + 0.5)) / n;
    parts.push(
      yTo(
        revolve(
          [
            { x: r, y: x - t / 2, e: true },
            { x: 1.16 * r, y: x - t / 2 + c, e: true },
            { x: 1.16 * r, y: x + t / 2 - c, e: true },
            { x: r, y: x + t / 2, e: true },
          ],
          seg,
          false,
        ),
        'x',
      ),
    );
  }
  return merge(...parts);
}

/**
 * v1 screw flight along +X centred on 0 — now a true `helicoid` on a core.
 * Pass the VISIBLE length only (a feed throat, an open trough). v1 envelope.
 * Tris ≈ 72 per turn + 40.
 */
export function screwFlight(r: number, len: number, turns: number, coreR: number): Mesh {
  // 12 steps per turn: v1 builders pass whole barrel lengths (hidden inside the
  // barrel), so the flight is kept at v1's cost; a visible flight built new
  // should use `helicoid` at its default 16.
  const st = LOD === 1 ? 8 : 12;
  // The flight plate's thickness straddles each end of the helix, so it is
  // swept over len − thick: the flight ends flush with the core instead of
  // standing proud of the barrel it lives in.
  const thick = Math.min(0.6, r * 0.12);
  return merge(cylinderX(coreR, len, 10), helicoid(coreR * 0.98, r, len - thick, turns, { steps: st, thick }));
}

// ─── Fittings (§3.8) ──────────────────────────────────────────────────────

/**
 * Flange proportions for pipe outer radius r (approx. EN 1092-1 PN16,
 * visually exaggerated: the raised-face height reads at plant scale).
 */
export function flangeDims(r: number): { R: number; t: number; rf: number; rfH: number; bc: number; bolts: number; af: number } {
  const R = 1.35 * r + 1.6;
  return {
    R,
    t: Math.max(0.6, 0.16 * R),
    rf: 0.66 * R,
    rfH: Math.max(0.25, 0.04 * R),
    bc: 0.8 * R,
    bolts: R < 3.5 ? 4 : R < 7 ? 8 : R < 11 ? 12 : 16,
    af: 0.16 * R,
  };
}

/** Hex nuts on a bolt circle, standing on y = 0 facing +Y. */
function boltRing(n: number, rc: number, af: number, h: number): Mesh {
  const parts: Mesh[] = [];
  for (let k = 0; k < n; k++) {
    const a = (TAU * (k + 0.5)) / n;
    parts.push(translate(hexPrism(af, h), rc * Math.cos(a), 0, rc * Math.sin(a)));
  }
  return merge(...parts);
}

/**
 * A raised-face flange for a pipe of outer radius r, facing +Y with its back
 * at y = 0 (a weld-neck `hub` tapers below it, to y = −0.9 t). Back, rim,
 * 45° chamfer, face, raised face (closed with `blind`). Bolts 'auto' show at
 * R ≥ 5 (lod 0). Zones: flange `mat` (default trim), bolts fastener.
 * Tris 12 · seg (+2 · seg hub, +16 per bolt); seg 12 → 144.
 */
export function flangeRF(
  r: number,
  o: { seg?: number; bolts?: 'auto' | 'show' | 'none'; hub?: boolean; blind?: boolean; mat?: MatKey } = {},
): Part[] {
  const fd = flangeDims(r);
  const seg = o.seg ?? segFor(fd.R, 'detail');
  const c = Math.min(0.3 * fd.t, 0.08 * fd.R);
  const prof: ProfilePt[] = [];
  if (o.hub) {
    const hl = 0.9 * fd.t;
    prof.push({ x: r, y: -hl, e: true }, { x: Math.min(fd.R * 0.8, r + 0.45 * (fd.R - r)), y: 0, e: true });
  } else prof.push({ x: r, y: 0, e: true });
  prof.push(
    { x: fd.R, y: 0, e: true },
    { x: fd.R, y: fd.t - c, e: true },
    { x: fd.R - c, y: fd.t, e: true },
    { x: fd.rf, y: fd.t, e: true },
    { x: fd.rf, y: fd.t + fd.rfH, e: true },
    { x: o.blind ? 0 : r, y: fd.t + fd.rfH, e: true },
  );
  const parts: Part[] = [{ mesh: revolve(prof, seg, false), mat: o.mat ?? zm('trim') }];
  const show = o.bolts === 'show' || ((o.bolts ?? 'auto') === 'auto' && fd.R >= 5 && LOD === 0);
  if (show) parts.push({ mesh: translate(boltRing(fd.bolts, fd.bc, fd.af, 0.6 * fd.af), 0, fd.t, 0), mat: zm('fastener') });
  return parts;
}

/**
 * v1 flange disc of radius r and thickness t (base on y = 0): now a chamfered
 * disc (r < 3, 6 · seg) or a disc with a raised face inside t (8 · seg).
 * Same envelope.
 */
export function flange(r: number, t = 1.6, seg = 14): Mesh {
  const c = Math.min(0.25 * t, 0.1 * r);
  if (r < 3)
    return revolve(
      [
        { x: 0, y: 0 },
        { x: r, y: 0, e: true },
        { x: r, y: t - c, e: true },
        { x: r - c, y: t, e: true },
        { x: 0, y: t },
      ],
      seg,
      false,
    );
  const rfH = Math.min(Math.max(0.25, 0.04 * r), 0.3 * t);
  return revolve(
    [
      { x: 0, y: 0 },
      { x: r, y: 0, e: true },
      { x: r, y: t - rfH, e: true },
      { x: 0.66 * r, y: t - rfH, e: true },
      { x: 0.66 * r, y: t, e: true },
      { x: 0, y: t },
    ],
    seg,
    false,
  );
}

/**
 * Two RF flanges face to face about y = 0 with a 0.3 rubber gasket between —
 * the joint reads as a dark line. Bolts at hero as nuts on both backs.
 * Tris ≈ 26 · seg (+ bolts).
 */
export function flangePair(r: number, o: { gasket?: boolean; bolts?: 'auto' | 'show' | 'none'; seg?: number; mat?: MatKey } = {}): Part[] {
  const fd = flangeDims(r);
  const T = fd.t + fd.rfH;
  const g = 0.15;
  const lo = flangeRF(r, { seg: o.seg, bolts: 'none', mat: o.mat });
  moveA(lo, 0, -T - g, 0);
  const hi = flangeRF(r, { seg: o.seg, bolts: 'none', mat: o.mat });
  turnA(hi, 'x', Math.PI);
  moveA(hi, 0, T + g, 0);
  const parts: Part[] = [...lo, ...hi];
  const seg = o.seg ?? segFor(fd.R, 'detail');
  if (o.gasket ?? true) parts.push({ mesh: revolve([{ x: fd.rf, y: -g, e: true }, { x: fd.rf, y: g, e: true }], seg, false), mat: zm('rubber') });
  const show = o.bolts === 'show' || ((o.bolts ?? 'auto') === 'auto' && fd.R >= 5 && LOD === 0);
  if (show) {
    const nb = boltRing(fd.bolts, fd.bc, fd.af, 0.6 * fd.af);
    const nb2 = rotateX(boltRing(fd.bolts, fd.bc, fd.af, 0.6 * fd.af), Math.PI);
    parts.push({ mesh: merge(translate(nb, 0, T + g, 0), translate(nb2, 0, -T - g, 0)), mat: zm('fastener') });
  }
  return assy(parts);
}

/**
 * A shell nozzle at `at` (on the shell surface) pointing along `n`: optional
 * reinforcing pad sitting ON the shell, pipe stub, weld-neck hub and RF
 * flange, projecting `proj` (default max(3, 1.1 · flange R)). The returned
 * port sits on the flange face, with `r` filled. Zones: `mat` (default
 * product), bolts fastener. Tris ≈ 200 at 12 seg (+ pad 24, + bolts).
 */
export function nozzleRF(o: {
  at: Vec3;
  n: Vec3;
  r: number;
  proj?: number;
  pad?: boolean;
  bolts?: 'auto' | 'show' | 'none';
  blind?: boolean;
  mat?: MatKey;
}): { parts: Part[]; port: Port3 } {
  const fd = flangeDims(o.r);
  const proj = o.proj ?? Math.max(3, 1.1 * fd.R);
  const T = fd.t + fd.rfH;
  const hl = 0.9 * fd.t;
  const seg = segFor(fd.R, 'detail');
  const mat = o.mat ?? zm('product');
  const parts: Part[] = [];
  const stubTop = Math.max(0.2, proj - T - hl);
  parts.push({ mesh: cylinder(o.r, stubTop, { seg, capTop: false, capBottom: false }), mat });
  if (o.pad) parts.push({ mesh: revolve([{ x: 1.7 * o.r, y: 0, e: true }, { x: 1.55 * o.r, y: 0.5, e: true }, { x: o.r, y: 0.5, e: true }], seg, false), mat });
  const fl = flangeRF(o.r, { seg, hub: true, bolts: o.bolts, blind: o.blind, mat });
  parts.push(...moveA(fl, 0, proj - T, 0));
  const nn = vNorm(o.n);
  const out = assy(parts);
  aimA(out, o.at, nn);
  return { parts: out, port: { p: vAdd(o.at, vMul(nn, proj)), n: nn, r: o.r } };
}

/**
 * v1 nozzle: a stub of pipe radius r and length `len` along `n` from `at`,
 * now with a weld-neck hub and the v1-sized flange (1.55 r × 1.6) at its
 * face (raised face when 1.55 r ≥ 3). Same envelope. Tris ≈ 10–14 · seg.
 */
export function nozzle(at: Vec3, n: Vec3, r: number, len: number): Mesh {
  const ft = Math.min(1.6, len * 0.8);
  const hub = Math.min(0.8, 0.3 * Math.max(0.1, len - ft));
  const stub = Math.max(0.05, len - ft - hub);
  const m = merge(
    cylinder(r, stub, { seg: 12, capTop: false, capBottom: false }),
    translate(frustum(r, 1.2 * r, hub, { seg: 12, capTop: false, capBottom: false }), 0, stub, 0),
    translate(flange(r * 1.55, ft, 12), 0, len - ft, 0),
  );
  return aim(m, at, n);
}

/**
 * A true circular elbow: centre-line from the origin along +X bending toward
 * +Y with bend radius R (default 3 r, long radius), 6 steps per 90°. Ends are
 * features; 'flange' adds RF flanges (merged, one material).
 * Tris 2 · seg · steps (12 × 6 → 144).
 */
export function bend(r: number, R = 3 * r, angle = Math.PI / 2, o: { seg?: number; steps?: number; ends?: 'plain' | 'flange' } = {}): Mesh {
  const seg = o.seg ?? 12;
  const steps = o.steps ?? Math.max(2, Math.round((6 * angle) / (Math.PI / 2)));
  const path: Vec3[] = [];
  for (let k = 0; k <= steps; k++) {
    const t = (angle * k) / steps;
    path.push(v3(R * Math.sin(t), R * (1 - Math.cos(t)), 0));
  }
  const m = tube(path, r, seg, false);
  if (o.ends !== 'flange') return m;
  const f0 = merge(...flangeRF(r, { seg }).map((q) => q.mesh));
  aim(f0, v3(0, 0, 0), v3(-1, 0, 0));
  const f1 = merge(...flangeRF(r, { seg }).map((q) => q.mesh));
  aim(f1, path[path.length - 1], v3(Math.cos(angle), Math.sin(angle), 0));
  return merge(m, f0, f1);
}

/**
 * A pipe reducer along +X from x = 0 (radius r0) to x = len (radius r1):
 * short straight, cone, short straight; `eccentric` keeps the bottom flat.
 * Transition rows featured. Tris 6 · seg.
 */
export function reducer(r0: number, r1: number, len: number, o: { eccentric?: boolean; seg?: number } = {}): Mesh {
  const seg = o.seg ?? 12;
  const row = (x: number, r: number): Vec3[] => {
    const cy = o.eccentric ? r - r0 : 0;
    const out: Vec3[] = [];
    for (let k = 0; k < seg; k++) {
      const a = (TAU * k) / seg;
      out.push(v3(x, cy + r * Math.sin(a), r * Math.cos(a)));
    }
    return out;
  };
  const rows = [row(0, r0), row(0.2 * len, r0), row(0.8 * len, r1), row(len, r1)];
  // Hints: radial from each row's own centre line.
  const m = emptyMesh();
  for (let j = 0; j < 3; j++)
    for (let i = 0; i < seg; i++) {
      const i1 = (i + 1) % seg;
      const a = rows[j][i];
      const b = rows[j][i1];
      const c = rows[j + 1][i1];
      const d = rows[j + 1][i];
      const cyA = o.eccentric ? [r0, r0, r1, r1][j] - r0 : 0;
      const cyB = o.eccentric ? [r0, r0, r1, r1][j + 1] - r0 : 0;
      const ctr = vMul(vAdd(vAdd(a, b), vAdd(c, d)), 0.25);
      quad(m, a, b, c, d, { hint: v3(0, ctr.y - (cyA + cyB) / 2, ctr.z), edges: (j === 0 ? 1 : 0) | 4 });
    }
  return featurize(m);
}

/**
 * A valve handwheel of radius r lying in the XZ plane (facing +Y) centred on
 * the origin: diamond-section rim (16-gon; 12-gon when r < 4), flat spokes,
 * hub, and a spinner knob at r ≥ 6. Zone: actuator. Tris ≈ 150–210.
 */
export function handwheel(r: number, o: { spokes?: 3 | 4 | 5; seg?: number; mat?: ZoneMap } = {}): Part[] {
  const n = o.seg ?? (r < 4 ? 12 : 16);
  const rr = Math.max(0.2, 0.08 * r);
  const rim: Vec3[] = [];
  for (let k = 0; k <= n; k++) {
    const a = (TAU * k) / n;
    rim.push(v3(r * Math.cos(a), 0, r * Math.sin(a)));
  }
  rim[n] = rim[0];
  const sec: Vec2[] = [
    { x: rr, y: 0 },
    { x: 0, y: rr },
    { x: -rr, y: 0 },
    { x: 0, y: -rr },
  ];
  const parts: Mesh[] = [sweep(rim, sec, { closed: true, creaseDeg: 60, up: v3(0, 1, 0) })];
  const ns = o.spokes ?? 3;
  const hr = Math.max(0.35, 0.14 * r);
  for (let k = 0; k < ns; k++) {
    const a = (TAU * k) / ns + Math.PI / 2;
    parts.push(member(v3(hr * 0.8 * Math.cos(a), 0, hr * 0.8 * Math.sin(a)), v3((r - rr * 0.5) * Math.cos(a), 0, (r - rr * 0.5) * Math.sin(a)), 'FLAT', Math.max(0.4, 0.12 * r)));
  }
  parts.push(translate(cylinder(hr, 0.3 * r, { seg: 8, capBottom: false }), 0, -0.15 * r, 0));
  if (r >= 6) parts.push(translate(cylinder(0.25 * rr + 0.2, 0.35 * r, { seg: 6, capBottom: false }), r, 0, 0));
  return [{ mesh: merge(...parts), mat: zm('actuator', o.mat) }];
}

/**
 * A quarter-turn lever from the origin along +X: flat bar, cranked end and a
 * rubber grip. Zones: actuator, rubber. Tris ≈ 48.
 */
export function lever(len: number, o: { grip?: boolean; mat?: ZoneMap } = {}): Part[] {
  const s = Math.max(0.6, 0.1 * len);
  const bar = merge(member(v3(0, 0, 0), v3(0.72 * len, 0, 0), 'FLAT', s), member(v3(0.72 * len, 0, 0), v3(len, 0.12 * len, 0), 'FLAT', s));
  const parts: Part[] = [{ mesh: bar, mat: zm('actuator', o.mat) }];
  if (o.grip ?? true) {
    const a = v3(0.74 * len, 0.006 * len, 0);
    const b = v3(len * 0.99, 0.118 * len, 0);
    parts.push({ mesh: tube([a, b], s * 0.42, 6, true), mat: zm('rubber', o.mat) });
  }
  return parts;
}

/**
 * An ISO 15552-style pneumatic cylinder along +X (default) from x = 0 to len,
 * axis at y = 0: square end caps (chamfered corners), square alu profile
 * barrel, bright piston rod extending `rod` (default 0.6 len), rod-end clevis,
 * two port fittings, tie rods at r ≥ 4. Zones: trim, alu, bright, fastener.
 * Tris ≈ 130 (+32 tie rods).
 */
export function pneuCylinder(len: number, r: number, o: { axis?: Axis; rod?: number; tieRods?: boolean; clevis?: boolean; mat?: ZoneMap } = {}): Part[] {
  const capS = 2.1 * r;
  const capL = Math.max(0.6, Math.min(0.12 * len, 0.8 * r));
  const rodL = o.rod ?? 0.6 * len;
  const rr = Math.max(0.15, 0.3 * r);
  const capSec = secChamferRect(capS, capS, 0.18 * capS);
  const trim: Mesh[] = [
    prism(capSec, v3(0, 0, 0), v3(capL, 0, 0), { up: v3(0, 1, 0) }),
    prism(capSec, v3(len - capL, 0, 0), v3(len, 0, 0), { up: v3(0, 1, 0) }),
  ];
  const alu = prism(secChamferRect(1.8 * r, 1.8 * r, 0.35 * r), v3(capL, 0, 0), v3(len - capL, 0, 0), { caps: false, up: v3(0, 1, 0) });
  const bright: Mesh[] = [yTo(revolve([{ x: rr, y: len }, { x: rr, y: len + rodL, e: true }, { x: 0, y: len + rodL }], 8, false), 'x')];
  if (o.clevis ?? true) trim.push(translate(box(0.9 * r, 0.9 * r, 0.9 * r), len + rodL + 0.45 * r, -0.45 * r, 0));
  const fast: Mesh[] = [
    translate(extrude(ngon(0.25 * r, 6), 0.35 * r, { caps: 'top' }), capL * 0.5, capS / 2, 0),
    translate(extrude(ngon(0.25 * r, 6), 0.35 * r, { caps: 'top' }), len - capL * 0.5, capS / 2, 0),
  ];
  if (o.tieRods ?? (r >= 4 && LOD === 0))
    for (const [y, z] of [
      [0.8, 0.8],
      [-0.8, 0.8],
      [0.8, -0.8],
      [-0.8, -0.8],
    ] as [number, number][])
      bright.push(prism(secRect(0.16 * r, 0.16 * r), v3(capL, y * r, z * r), v3(len - capL, y * r, z * r), { caps: false, up: v3(0, 1, 0) }));
  const parts = assy(
    { mesh: merge(...trim), mat: zm('trim', o.mat) },
    { mesh: alu, mat: zm('alu', o.mat) },
    { mesh: merge(...bright), mat: zm('bright', o.mat) },
    { mesh: merge(...fast), mat: zm('fastener', o.mat) },
  );
  const axis = o.axis ?? 'x';
  if (axis !== 'x') for (const q of parts) xTo(q.mesh, axis);
  return parts;
}

/**
 * A control-valve diaphragm actuator standing on y = 0 (the bonnet): a yoke of
 * two posts and a bridge (height `yoke`, default 1.3 r), the double-dished
 * casing (radius r, Korbbogen-style dishes, bolted lip as feature lines), the
 * stem with a travel indicator and a positioner box. Zones: actuator (casing),
 * cast (yoke), bright (stem), alu (positioner). Tris ≈ 300.
 */
export function diaphragmActuator(r: number, o: { yoke?: number; positioner?: boolean; mat?: ZoneMap } = {}): Part[] {
  const yk = o.yoke ?? 1.3 * r;
  const seg = Math.min(16, segFor(r, 'body'));
  const lipT = Math.max(0.3, 0.06 * r);
  const hp = headProfile('korbbogen', r, { steps: 3, sf: 0 });
  const s = 0.55;
  const fy = yk + 0.3 * r;
  const prof: ProfilePt[] = [];
  for (let i = hp.length - 1; i >= 0; i--) prof.push({ x: hp[i].x, y: fy - lipT - hp[i].y * s, e: i === 0 });
  prof.push({ x: 1.08 * r, y: fy - lipT, e: true }, { x: 1.08 * r, y: fy + lipT, e: true }, { x: r, y: fy + lipT, e: true });
  for (let i = 1; i < hp.length; i++) prof.push({ x: hp[i].x, y: fy + lipT + hp[i].y * s });
  const casing = revolve(prof, seg, false);
  const ps = Math.max(0.5, 0.14 * r);
  const cast = merge(
    member(v3(0.45 * r, 0, 0), v3(0.45 * r, yk, 0), 'SHS', ps),
    member(v3(-0.45 * r, 0, 0), v3(-0.45 * r, yk, 0), 'SHS', ps),
    translate(box(1.1 * r, 0.12 * r, 0.32 * r), 0, yk - 0.12 * r, 0),
    box(1.1 * r, 0.1 * r, 0.32 * r),
  );
  const sr = Math.max(0.15, 0.06 * r);
  const bright = merge(cylinder(sr, fy - 0.3 * r, { seg: 6, capBottom: false }), translate(box(0.28 * r, 0.08 * r, 0.1 * r), 0, yk * 0.55, 0));
  const parts: Part[] = [
    { mesh: casing, mat: zm('actuator', o.mat) },
    { mesh: cast, mat: zm('cast', o.mat) },
    { mesh: bright, mat: zm('bright', o.mat) },
  ];
  if (o.positioner ?? true)
    parts.push({ mesh: translate(roundBox(0.5 * r, 0.45 * r, 0.36 * r, 0.06 * r, { seg: 1, top: 'bevel' }), 0.45 * r + 0.3 * r, yk * 0.35, -0.1 * r), mat: zm('alu', o.mat) });
  return assy(parts);
}

/**
 * A manway at `at` on a shell, pointing along `n`: neck of radius r, bolted
 * cover flange (the joint a feature line), two hinge knuckles, a handle and
 * cover bolts ('auto': at 1.25 r ≥ 5). Zones: product (neck/cover), trim
 * (hinge/handle), fastener. Tris ≈ 190 (+ bolts).
 */
export function manway(o: { at: Vec3; n: Vec3; r: number; hinge?: 'side' | 'top'; davit?: boolean; bolts?: 'auto' | 'show' | 'none'; mat?: MatKey }): Part[] {
  const r = o.r;
  const Rf = 1.25 * r;
  const seg = segFor(Rf, 'detail');
  const nh = Math.max(2, 0.5 * r);
  const ft = Math.max(0.5, 0.12 * r);
  const ct = Math.max(0.5, 0.12 * r);
  const c = Math.min(0.3 * ct, 0.1 * Rf);
  const prod = merge(
    cylinder(r, nh - ft, { seg, capTop: false, capBottom: false }),
    revolve(
      [
        { x: r, y: nh - ft },
        { x: Rf, y: nh - ft, e: true },
        { x: Rf, y: nh, e: true },
        { x: Rf, y: nh + ct - c, e: true },
        { x: Rf - c, y: nh + ct, e: true },
        { x: 0, y: nh + ct },
      ],
      seg,
      false,
    ),
  );
  const trim: Mesh[] = [];
  const hx = (o.hinge ?? 'side') === 'side' ? 1 : 0;
  const hz = hx ? 0 : -1;
  for (const s of [-1, 1]) {
    const kn = hx ? cylinderZ(Math.max(0.3, 0.08 * r), 0.3 * r, 6) : cylinderX(Math.max(0.3, 0.08 * r), 0.3 * r, 6);
    trim.push(translate(kn, hx * (Rf + 0.2) + (hx ? 0 : s * 0.4 * r), nh + ct * 0.5, hz * (Rf + 0.2) + (hx ? s * 0.4 * r : 0)));
  }
  trim.push(translate(box(0.4 * r, 0.12 * r, 0.12 * r), -hx * (Rf * 0.7), nh + ct, -hz * (Rf * 0.7)));
  const parts: Part[] = [
    { mesh: prod, mat: o.mat ?? zm('product') },
    { mesh: merge(...trim), mat: zm('trim') },
  ];
  const show = o.bolts === 'show' || ((o.bolts ?? 'auto') === 'auto' && Rf >= 5 && LOD === 0);
  if (show) parts.push({ mesh: translate(boltRing(Rf < 8 ? 8 : 12, 1.12 * r, 0.12 * Rf, 0.35), 0, nh + ct, 0), mat: zm('fastener') });
  const out = assy(parts);
  return aimA(out, o.at, vNorm(o.n));
}

/**
 * A rectangular inspection hatch / door at `at` facing `n`: w wide, h tall
 * (for a wall-mounted hatch), frame, bevelled cover, two hinges and
 * quick-release clamps. Zones: `mat` (default product), trim. Tris ≈ 110.
 */
export function hatch(o: { at: Vec3; n: Vec3; w: number; h: number; hinge?: 'top' | 'side'; clamps?: number; mat?: MatKey }): Part[] {
  const { w, h } = o;
  const t = Math.max(0.4, 0.06 * Math.min(w, h));
  const fh = 0.4;
  const frame = emptyMesh();
  const X = w / 2;
  const Z = h / 2;
  const x = X - t;
  const z = Z - t;
  const ring4 = (A: Vec3[], B: Vec3[], n: Vec3 | null) => {
    for (let i = 0; i < 4; i++) {
      const i1 = (i + 1) % 4;
      const ctr = vMul(vAdd(vAdd(A[i], A[i1]), vAdd(B[i1], B[i])), 0.25);
      quad(frame, A[i], A[i1], B[i1], B[i], n ? { n } : { hint: v3(ctr.x, 0, ctr.z) });
    }
  };
  const outerB = [v3(-X, 0, -Z), v3(X, 0, -Z), v3(X, 0, Z), v3(-X, 0, Z)];
  const outerT = outerB.map((q) => v3(q.x, fh, q.z));
  const innerT = [v3(-x, fh, -z), v3(x, fh, -z), v3(x, fh, z), v3(-x, fh, z)];
  ring4(outerB, outerT, null);
  ring4(outerT, innerT, v3(0, 1, 0));
  featurize(frame);
  const cover = translate(bevelBox(w - 1.2 * t, 0.5, h - 1.2 * t, 0.2), 0, fh, 0);
  const trim: Mesh[] = [];
  const kr = Math.max(0.3, 0.05 * Math.min(w, h));
  const top = (o.hinge ?? 'top') === 'top';
  for (const s of [-0.3, 0.3]) {
    if (top) trim.push(translate(cylinderX(kr, 0.18 * w, 6), s * w, fh + 0.25, -Z - kr * 0.5));
    else trim.push(translate(cylinderZ(kr, 0.18 * h, 6), -X - kr * 0.5, fh + 0.25, s * h));
  }
  const nc = o.clamps ?? 2;
  for (let k = 0; k < nc; k++) {
    const f = nc === 1 ? 0 : -0.3 + (0.6 * k) / (nc - 1);
    trim.push(top ? translate(box(0.08 * w, 0.4, 0.8), f * w, fh + 0.3, Z - 0.3) : translate(box(0.8, 0.4, 0.08 * h), X - 0.3, fh + 0.3, f * h));
  }
  const out = assy({ mesh: merge(frame, cover), mat: o.mat ?? zm('product') }, { mesh: merge(...trim), mat: zm('trim') });
  return aimA(out, o.at, vNorm(o.n));
}

/**
 * A sight glass at `at` facing `n`. Round: a bolted ring frame with the glass
 * disc proud of it (DECAL; bolts at 1.35 r ≥ 5). Slot: a vertical frame
 * `len` tall with a glass strip. Zones: trim (frame), glass. Tris ≈ 60 / 26.
 */
export function sightGlass(o: { at: Vec3; n: Vec3; r: number; style?: 'round' | 'slot'; len?: number; mat?: MatKey }): Part[] {
  const r = o.r;
  const parts: Part[] = [];
  if ((o.style ?? 'round') === 'round') {
    const Rr = 1.35 * r;
    const seg = segFor(Rr, 'detail');
    parts.push({ mesh: revolve([{ x: Rr, y: 0 }, { x: Rr, y: 0.5, e: true }, { x: r, y: 0.5, e: true }], seg, false), mat: o.mat ?? zm('trim') });
    parts.push({ mesh: decal(revolve([{ x: r, y: 0.75 }, { x: 0, y: 0.75 }], seg, false)), mat: zm('glass') });
    if (Rr >= 5 && LOD === 0) parts.push({ mesh: translate(boltRing(6, 1.17 * r, 0.12 * Rr, 0.3), 0, 0.5, 0), mat: zm('fastener') });
  } else {
    const L = o.len ?? 4 * r;
    const wd = 2 * r;
    const f = hatchFrame(wd, L, Math.max(0.3, 0.25 * r), 0.35);
    parts.push({ mesh: f, mat: o.mat ?? zm('trim') });
    parts.push({ mesh: decal(plateQuad(v3(0, 0.3, 0), v3(wd / 2 - 0.2, 0, 0), v3(0, 0, L / 2 - 0.2), v3(0, 1, 0))), mat: zm('glass') });
  }
  const out = assy(parts);
  return aimA(out, o.at, vNorm(o.n));
}

/** A picture frame (outer w × h, bar t, height fh) in the XZ plane facing +Y. */
function hatchFrame(w: number, h: number, t: number, fh: number): Mesh {
  const m = emptyMesh();
  const X = w / 2;
  const Z = h / 2;
  const x = X - t;
  const z = Z - t;
  const oB = [v3(-X, 0, -Z), v3(X, 0, -Z), v3(X, 0, Z), v3(-X, 0, Z)];
  const oT = oB.map((q) => v3(q.x, fh, q.z));
  const iT = [v3(-x, fh, -z), v3(x, fh, -z), v3(x, fh, z), v3(-x, fh, z)];
  const iB = iT.map((q) => v3(q.x, 0, q.z));
  for (let i = 0; i < 4; i++) {
    const i1 = (i + 1) % 4;
    const mo = vMul(vAdd(oB[i], oB[i1]), 0.5);
    quad(m, oB[i], oB[i1], oT[i1], oT[i], { hint: v3(mo.x, 0, mo.z) });
    quad(m, oT[i], oT[i1], iT[i1], iT[i], { n: v3(0, 1, 0) });
    quad(m, iT[i], iT[i1], iB[i1], iB[i], { hint: v3(-mo.x, 0, -mo.z) });
  }
  return featurize(m);
}

function namePlateMesh(at: Vec3, n: Vec3, w: number, h: number): Mesh {
  return aim(decal(openBox(w, 0.3, h)), at, vNorm(n));
}

/** A stainless rating / name plate at `at` facing `n`, w × h (h vertical for a wall plate). DECAL. 10 tris. */
export function namePlate(o: { at: Vec3; n: Vec3; w: number; h: number; mat?: MatKey }): Part[] {
  return [{ mesh: namePlateMesh(o.at, o.n, o.w, o.h), mat: o.mat ?? zm('product') }];
}

// ─── Electrical, operator side, services (§3.9) ───────────────────────────

/**
 * An emergency stop facing +Y: a yellow push-button box with the red
 * mushroom head on top (red on yellow, ISO 13850). `r` = mushroom radius.
 * Zones: guard, signal. Tris ≈ 50.
 */
export function estop(r: number, o: { mat?: ZoneMap } = {}): Part[] {
  const base = openBox(2.2 * r, 1.3 * r, 2.2 * r);
  const head = revolve(
    [
      { x: 0.42 * r, y: 1.3 * r, e: true },
      { x: r, y: 1.55 * r, e: true },
      { x: r, y: 1.8 * r, e: true },
      { x: 0, y: 2.0 * r },
    ],
    8,
    false,
  );
  return assy({ mesh: base, mat: zm('guard', o.mat) }, { mesh: head, mat: zm('signal', o.mat) });
}

/**
 * A signal tower on a stem (y = 0 up): green / amber / red tiers from the
 * bottom (red on top), featured joints. Tris ≈ 68.
 */
export function stackLight(r: number, tiers = 3, o: { mat?: ZoneMap } = {}): Part[] {
  const stemH = 1.5 * r;
  const th = 1.1 * r;
  // Bottom to top: green, amber, then blue/white for extra tiers; red on top.
  const seq: MatKey[] = ['signalGreen', 'safety', 'screen', 'plastic'];
  const cols: MatKey[] = [];
  for (let k = 0; k < Math.max(0, tiers - 1); k++) cols.push(seq[k % seq.length]);
  cols.push(zm('signal', o.mat));
  const parts: Part[] = [{ mesh: cylinder(0.3 * r, stemH, { seg: 6, capTop: false, capBottom: false }), mat: zm('trim', o.mat) }];
  cols.forEach((mat, k) => {
    const top = k === cols.length - 1;
    parts.push({ mesh: translate(cylinder(r, th, { seg: 8, capBottom: k === 0, capTop: top }), 0, stemH + k * th, 0), mat });
  });
  return assy(parts);
}

/**
 * An HMI panel: bezel housing (w wide, h tall, rounded vertical edges),
 * dark glossy display proud of its front (−Z, operator side, DECAL), tilted
 * back by `tilt` (default 0.2 rad); on a swing-arm post `arm` tall when given
 * (post foot at y = 0), else the housing bottom sits at y = 0.
 * Zones: bezel, display, trim. Tris ≈ 75 (+ arm 28).
 */
export function hmiPanel(o: { w: number; h?: number; arm?: number; tilt?: number; bezel?: number; mat?: ZoneMap }): Part[] {
  const w = o.w;
  const hh = o.h ?? 0.72 * w;
  const dep = o.bezel ?? Math.max(0.8, 0.1 * w);
  const tilt = o.tilt ?? 0.2;
  const housing = roundBox(w, hh, dep, Math.min(0.12 * Math.min(w, hh), 0.45 * dep), { seg: 1, top: 'bevel', bottom: true });
  const disp = decal(plateQuad(v3(0, hh * 0.52, -dep / 2 - 0.2), v3(w * 0.4, 0, 0), v3(0, hh * 0.36, 0), v3(0, 0, -1)));
  const parts: Part[] = [
    { mesh: housing, mat: zm('bezel', o.mat) },
    { mesh: disp, mat: zm('display', o.mat) },
  ];
  for (const q of parts) {
    translate(q.mesh, 0, -hh * 0.5, 0);
    rotateX(q.mesh, tilt);
    translate(q.mesh, 0, hh * 0.5, 0);
  }
  const arm = o.arm ?? 0;
  if (arm > 0) {
    moveA(parts, 0, arm + 1.2, 0);
    parts.push({
      mesh: merge(cylinder(Math.max(0.4, 0.05 * w), arm, { seg: 8, capBottom: false }), translate(box(Math.max(1, 0.14 * w), 1.2, Math.max(1, 0.14 * w)), 0, arm, 0)),
      mat: zm('trim', o.mat),
    });
  }
  return assy(parts);
}

/**
 * A Rittal-style floor cabinet w × h × d on the floor, front = −Z: RAL 7035
 * body, door leaves (1 or 2) with featured seams and swing handles, roof
 * canopy, black 4-unit plinth, and optionally an HMI in the door, an e-stop and
 * a stack light on the roof. Zones: cabinet, bezel (+ hmi/estop/stack).
 * Tris ≈ 110 (+HMI 75, e-stop 50, stack 68).
 */
export function cabinet(o: {
  w: number;
  h: number;
  d: number;
  doors?: 1 | 2;
  plinth?: boolean;
  canopy?: boolean;
  hmi?: boolean;
  estop?: boolean;
  stack?: boolean;
  handle?: boolean;
  mat?: ZoneMap;
}): Part[] {
  const { w, h, d } = o;
  const ph = o.plinth ?? true ? Math.min(4, 0.08 * h) : 0;
  const chH = o.canopy ?? true ? Math.max(0.5, 0.02 * h) : 0;
  const bh = h - ph - chH;
  const cab: Mesh[] = [translate(bevelBox(w, bh, d, Math.min(0.4, 0.05 * Math.min(w, d))), 0, ph, 0)];
  const bez: Mesh[] = [];
  if (ph > 0) bez.push(extrude(rectPoly(w * 0.98, d * 0.98), ph, { caps: 'none' }));
  const nd = o.doors ?? (w > 0.55 * h ? 2 : 1);
  const margin = Math.min(0.6, 0.04 * w);
  const gap = 0.35;
  const lw = (w - 2 * margin - (nd - 1) * gap) / nd;
  const dh = bh - 2 * margin;
  for (let k = 0; k < nd; k++) {
    const cx = -w / 2 + margin + lw / 2 + k * (lw + gap);
    const leaf = rotateX(openBox(lw, 0.3, dh), -Math.PI / 2);
    cab.push(translate(leaf, cx, ph + margin + dh / 2, -d / 2));
    if (o.handle ?? true) {
      const hx = nd === 2 ? (k === 0 ? cx + lw / 2 - 0.9 : cx - lw / 2 + 0.9) : cx + lw / 2 - 0.9;
      bez.push(translate(rotateX(openBox(0.4, 0.4, Math.max(1, 0.14 * dh)), -Math.PI / 2), hx, ph + margin + dh * 0.52, -d / 2 - 0.3));
    }
  }
  if (chH > 0) cab.push(translate(bevelBox(w + 1, chH, d + 1, Math.min(0.2, chH * 0.4)), 0, h - chH, 0));
  const parts: Part[] = [
    { mesh: merge(...cab), mat: zm('cabinet', o.mat) },
    bez.length > 0 ? { mesh: merge(...bez), mat: zm('bezel', o.mat) } : { mesh: emptyMesh(), mat: zm('bezel', o.mat) },
  ];
  if (o.hmi) {
    const hw = Math.min(0.6 * (nd === 2 ? lw : lw * 0.8), 0.5 * h);
    const hp = hmiPanel({ w: hw, tilt: 0, bezel: 0.8, mat: o.mat });
    moveA(hp, -w / 2 + margin + lw / 2, ph + bh * 0.62, -d / 2 - 0.3 - 0.4);
    parts.push(...hp);
  }
  if (o.estop) {
    const er = Math.max(0.6, 0.04 * w);
    const es = aimA(estop(er, { mat: o.mat }), v3(-w / 2 + margin + lw / 2, ph + bh * 0.42, -d / 2 - 0.3), v3(0, 0, -1));
    parts.push(...es);
  }
  if (o.stack) parts.push(...moveA(stackLight(Math.max(0.6, 0.035 * w), 3, { mat: o.mat }), w / 2 - Math.max(1.5, 0.12 * w), h, d / 4));
  return assy(parts);
}

/**
 * v1 control cabinet with a fascia panel on its +Z face: same `{ body, face }`
 * and envelope, now a bevelled body with a roof canopy and a DECAL face.
 * Tris ≈ 54.
 */
export function controlBox(w: number, h: number, d: number): { body: Mesh; face: Mesh } {
  return {
    body: merge(bevelBox(w, h, d, clampN(0.05 * Math.min(w, h, d), 0.35, 1.5)), translate(box(w * 1.06, 1.4, d * 1.06), 0, h, 0)),
    face: decal(translate(box(w * 0.72, h * 0.34, 0.9), 0, h * 0.5, d / 2)),
  };
}

/** v1 HMI screen on a stalk: same `{ arm, screen }` and envelope, bevelled housing. Tris ≈ 68. */
export function hmi(w: number, h: number): { arm: Mesh; screen: Mesh } {
  return {
    arm: cylinder(1.1, h, { seg: 8, capBottom: false }),
    screen: translate(rotateX(bevelBox(w, w * 0.62, 1.2, 0.1, { bottom: true }), -0.32), 0, h, 0),
  };
}

/** A small wall/frame junction box standing on two bottom cable glands (bezel). Zones: cabinet, bezel. Tris ≈ 80. */
export function junctionBox(w: number, h: number, d: number, o: { mat?: ZoneMap } = {}): Part[] {
  const gl = Math.max(0.6, 0.18 * h);
  const gr = Math.max(0.25, 0.1 * Math.min(w, d));
  const body = translate(bevelBox(w, h, d, clampN(0.05 * Math.min(w, h, d), 0.2, 0.6), { bottom: true }), 0, gl, 0);
  const glands = merge(translate(cylinder(gr, gl, { seg: 6, capTop: false }), -w * 0.25, 0, 0), translate(cylinder(gr, gl, { seg: 6, capTop: false }), w * 0.25, 0, 0));
  return assy({ mesh: body, mat: zm('cabinet', o.mat) }, { mesh: glands, mat: zm('bezel', o.mat) });
}

/**
 * A cable tray from a to b (a→b is the tray's bottom centre line), width w:
 * 'ladder' = two channel side rails (toes inward) and rungs every
 * `rungPitch` (12); 'trough' = a U-section prism. Tris: ladder 56 + 12/rung,
 * trough 28.
 */
export function cableTray(a: Vec3, b: Vec3, w: number, o: { h?: number; kind?: 'ladder' | 'trough'; rungPitch?: number } = {}): Mesh {
  const h = o.h ?? Math.max(1.5, 0.25 * w);
  const dv = vSub(b, a);
  const L = Math.hypot(dv.x, dv.y, dv.z);
  if (L < 1e-3) return emptyMesh();
  const d = vMul(dv, 1 / L);
  const s = Math.abs(d.y) > 0.9 ? v3(1, 0, 0) : vNorm(vCross(d, v3(0, 1, 0)));
  if ((o.kind ?? 'ladder') === 'trough') {
    const t = Math.max(0.2, 0.06 * w);
    const sec: Vec2[] = [
      { x: -w / 2, y: 0 },
      { x: w / 2, y: 0 },
      { x: w / 2, y: h },
      { x: w / 2 - t, y: h },
      { x: w / 2 - t, y: t },
      { x: -w / 2 + t, y: t },
      { x: -w / 2 + t, y: h },
      { x: -w / 2, y: h },
    ];
    return prism(sec, a, b, { up: v3(0, 1, 0) });
  }
  const up = v3(0, h / 2, 0);
  const parts: Mesh[] = [];
  for (const sg of [-1, 1]) {
    const off = vAdd(vMul(s, (sg * w) / 2), up);
    parts.push(member(vAdd(a, off), vAdd(b, off), 'C', h, { roll: sg > 0 ? Math.PI : 0 }));
  }
  const pitch = o.rungPitch ?? 12;
  const n = Math.max(1, Math.floor(L / pitch));
  for (let k = 0; k < n; k++) {
    const c = vAdd(a, vMul(d, (L * (k + 0.5)) / n));
    const y = v3(0, 0.15 * h, 0);
    parts.push(member(vAdd(vAdd(c, vMul(s, -w / 2 + 0.2 * h)), y), vAdd(vAdd(c, vMul(s, w / 2 - 0.2 * h)), y), 'FLAT', Math.max(0.5, 0.2 * h)));
  }
  return merge(...parts);
}

/**
 * A flexible hose through `path`: a Catmull-Rom curve with gravity sag
 * (fraction of each span, default 0.08), an 8-seg rubber tube and bright crimp
 * ferrules at both ends. Tris ≈ 16 per sample segment + 2 × 32.
 */
export function hose(path: Vec3[], r: number, o: { sag?: number; ferrules?: boolean; seg?: number; mat?: ZoneMap } = {}): Part[] {
  if (path.length < 2) return [];
  const sag = o.sag ?? 0.08;
  const seg = o.seg ?? 8;
  const P = [path[0], ...path, path[path.length - 1]];
  const pts: Vec3[] = [path[0]];
  const sub = 4;
  for (let i = 1; i < P.length - 2; i++) {
    const p0 = P[i - 1];
    const p1 = P[i];
    const p2 = P[i + 1];
    const p3 = P[i + 2];
    const span = Math.hypot(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z);
    for (let k = 1; k <= sub; k++) {
      const t = k / sub;
      const t2 = t * t;
      const t3 = t2 * t;
      const cr = (a: number, b: number, c: number, d: number) => 0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      pts.push(v3(cr(p0.x, p1.x, p2.x, p3.x), cr(p0.y, p1.y, p2.y, p3.y) - (k < sub ? sag * span * Math.sin(Math.PI * t) : 0), cr(p0.z, p1.z, p2.z, p3.z)));
    }
  }
  const parts: Part[] = [{ mesh: soft(tube(pts, r, seg, true)), mat: zm('rubber', o.mat) }];
  if (o.ferrules ?? true) {
    const fl = Math.max(0.6, 3 * r);
    const ends: [Vec3, Vec3][] = [
      [pts[0], vNorm(vSub(pts[1], pts[0]))],
      [pts[pts.length - 1], vNorm(vSub(pts[pts.length - 2], pts[pts.length - 1]))],
    ];
    const fm: Mesh[] = [];
    for (const [q, n] of ends) fm.push(aim(cylinder(1.3 * r, fl, { seg }), q, n));
    parts.push({ mesh: merge(...fm), mat: zm('bright', o.mat) });
  }
  return parts;
}

/**
 * v1 four castor wheels under a mobile unit — now swivel castors (top plate,
 * fork, wheel) in the v1 envelope, one mesh (callers draw it rubber).
 * Tris ≈ 4 × 64.
 */
export function castors(w: number, d: number, r = 2.6): Mesh {
  const one = (X: number, Z: number) => {
    const zc = Z + 0.9;
    const rw = 0.7 * r;
    const wheel = cylinderX(rw, 1.0, 8);
    translate(wheel, X - 0.9, rw, zc);
    const forkH = 1.75 * r - rw * 0.9;
    const forks = merge(
      translate(extrude(rectPoly(0.3, 0.8 * r), forkH, { caps: 'top' }), X - 1.65, rw * 0.9, zc),
      translate(extrude(rectPoly(0.3, 0.8 * r), forkH, { caps: 'top' }), X - 0.15, rw * 0.9, zc),
    );
    const plate = translate(box(1.8, 0.25 * r, 1.9 * r * 0.999), X - 0.9, 1.75 * r, zc);
    return merge(wheel, forks, plate);
  };
  return merge(one(w / 2 - r, d / 2 - r), one(-(w / 2 - r), d / 2 - r), one(w / 2 - r, -(d / 2 - r)), one(-(w / 2 - r), -(d / 2 - r)));
}

// ─── Remaining v1 exports ─────────────────────────────────────────────────

/** A quarter-ellipse vessel-end profile (r at y = 0 → apex at depth); the rim is a feature. */
export function dishProfile(r: number, depth: number, steps = 5): ProfilePt[] {
  const out: ProfilePt[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * (Math.PI / 2);
    out.push({ x: i === steps ? 0 : r * Math.cos(t), y: depth * Math.sin(t), e: i === 0 });
  }
  return out;
}

/** A hemispherical dome cap, for gas bottles and pressure vessels. */
export function dome(r: number, seg = 18): Mesh {
  return revolve(dishProfile(r, r, 6), seg, true);
}

/** A vertical inspection window let into a wall facing +Z — a bevelled pane (the chamfer reads as its frame). */
export function window3(w: number, h: number, z: number): Mesh {
  return translate(bevelBox(w, h, 0.8, Math.min(0.3, 0.2 * Math.min(w, h)), { bottom: true }), 0, 0, z);
}

/** A short length of pipe between two local points — internal cross-overs. */
export function pipeBetween(a: Vec3, b: Vec3, r: number, seg = 10): Mesh {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dy, dz);
  if (len < 1e-4) return emptyMesh();
  const m = cylinder(r, len, { seg });
  rotateX(m, Math.acos(Math.max(-1, Math.min(1, dy / len))));
  rotateY(m, Math.atan2(dx, dz));
  return translate(m, a.x, a.y, a.z);
}

/** Re-exported so model files import mesh helpers from one place. */
export {
  bevelBox,
  bounds,
  box,
  boxCentered,
  cylinder,
  cylinderX,
  cylinderZ,
  decal,
  densify,
  emptyMesh,
  extrude,
  featurize,
  featDih,
  featEdges,
  filletArc,
  filletPath,
  frustum,
  cone,
  helicoid,
  hexPrism,
  lined,
  merge,
  ngon,
  prism,
  quad,
  rectPoly,
  revolve,
  ring,
  rotateX,
  rotateY,
  rotateZ,
  roundBox,
  scale,
  soft,
  sphere,
  stadium,
  sweep,
  torusArc,
  translate,
  tri,
  triangulate,
  tube,
  F_DECAL,
  F_PAIR,
};
export { v3, vAdd, vCross, vDot, vMul, vNorm, vSub, TAU };
export type { Mesh, ProfilePt, Role, Vec2, Vec3 };
